'use client';

import { useEffect, useLayoutEffect, useState, useRef, useCallback, useReducer, useMemo, memo, type ReactNode } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/components/auth-provider';
import { useSignupGate } from '@/components/workspace/signup-gate';
import { useT, useTranslation } from '@/components/i18n-provider';
import { useUAMP } from '@/hooks/useUAMP';
import { useAnalytics } from '@/hooks/use-analytics';
import { cn, formatRelativeTime } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import { Avatar } from '@/components/avatar';
import { Spinner } from '@/components/ui/spinner';
import {
  X,
  Send,
  Paperclip,
  Plus,
  Pencil,
  Check,
  Users,
  Image as ImageIcon,
  FileText,
  Film,
  Music,
  Box,
  Upload,
  Mic,
  MicOff,
  Phone,
  PenSquare,
  Square,
  MoreHorizontal,
  Trash2,
  AlertCircle,
  RefreshCw,
  ChevronDown,
} from 'lucide-react';
import { BotIcon as Bot } from '@/components/icons/bot-icon';
import { StickToBottom, useStickToBottomContext, useStickToBottom } from 'use-stick-to-bottom';
import { Markdown, ThinkingBlock } from '@/components/markdown';
import { ToolCallStrip, type ToolStripItem } from '@/components/chat/tool-call-strip';
import { MessagingSendBubble } from '@/components/chat/messaging-send-bubble';
import { isMessagingSendTool, getMessagingSendBubbleProps } from '@/components/tool-renderers';
import { SmoothShrink } from '@/components/chat/smooth-shrink';
import { DocumentChip } from '@/components/chat/document-chip';
import {
  ChatHostProvider,
  type ChatHostHandlers,
} from '@/components/chat/chat-host-context';
import { emitDelegationEvent } from '@/lib/agents/delegation-events';
import { MessageLinkPreviews } from '@/components/chat/message-link-previews';
import { filterDisplayMessages } from '@/lib/chat/display-filter';
import { SubscribeDialog } from '@/components/subscribe-dialog';
import { ChatPaymentBanner } from '@/components/chat/chat-payment-banner';
import { deltaResumesHeldTurn, heldTurnFromDelta, type HeldTurn } from '@/components/chat/spending-hold';
import { APPROVAL_WAIT_NOTICE } from '@/components/chat/spending-hold';
import { ProviderIcon } from '@/components/provider-icon';
import {
  ImagePreview,
  VideoPreview,
  DocumentPreview,
  Model3DPreview,
  FileCard,
  Lightbox,
  type LightboxItem,
} from '@/components/content-previews';
import { AudioPlayer } from '@/components/post-media';
import { LinkBar } from '@/components/link-preview';
import { Tip } from '@/components/ui/tooltip';

// Diagnostic flag — when true, every reducer / WS handler / fetch / render
// gate that matters for the "streaming message ends in '...'" bug emits a
// `[stream-dbg]` log line. Flip to `false` once the cause is identified.
const STREAM_DBG = false;
const sdbg = (...args: unknown[]) => { if (STREAM_DBG) console.log('[stream-dbg]', ...args); };

/**
 * Turn a failed chat write into something worth showing.
 *
 * Most failures here are transient and "tap to retry" is the right
 * advice, but some are a decision the server made and will keep making:
 * the recipient is not accepting new messages, the chat is gone. Those
 * carry an `error` string that explains itself, and repeating "tap to
 * retry" over the top of one just invites the user to fail again.
 */
async function readChatError(res: Response): Promise<string> {
  const retry = 'Failed to send. Tap your message to retry.';
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body?.error === 'string' && body.error.trim()) return body.error;
  } catch {
    /* not JSON, or already consumed */
  }
  return retry;
}
// Dedup the dots-render log: log once per (messageId, status) transition so we
// don't spam the console on every re-render. Lives at module level so it
// survives across rerenders for the same message identity.
const dotsRenderSeen = new Set<string>();

/** Strip legacy content labels from old DB messages. New messages use structured content_items. */
const stripContentLabels = (text: string) => {
  if (text.includes('[content:')) {
    text = text.replace(/\s*\[content:[0-9a-f-]{36}\]/gi, '');
  }
  return text;
};
import { VoiceChatOverlay } from '@/components/voice-chat-overlay';
import { createAudioCapture, createVoiceCapture, type AudioCapture } from '@/lib/audio/capture';
import { emitWorkspaceSpawn } from '@/lib/workspaces/spawn-events';

interface TextContent { type: 'text'; text: string; }
interface ImageContent { type: 'image'; image: string | { url: string }; format?: string; alt_text?: string; content_id?: string; display_hint?: 'inline' | 'attachment' | 'sandbox'; }
interface AudioContent { type: 'audio'; audio: string | { url: string }; format?: string; duration_ms?: number; content_id?: string; display_hint?: 'inline' | 'attachment' | 'sandbox'; }
interface VideoContent { type: 'video'; video: string | { url: string }; format?: string; content_id?: string; display_hint?: 'inline' | 'attachment' | 'sandbox'; }
interface FileContent { type: 'file'; file: string | { url: string }; filename: string; mime_type: string; content_id?: string; display_hint?: 'inline' | 'attachment' | 'sandbox'; metadata?: Record<string, unknown>; }
interface ToolCallContent { type: 'tool_call'; tool_call: { id: string; name: string; arguments: string; }; }
interface ToolResultContent { type: 'tool_result'; tool_result: { call_id: string; result: string; is_error?: boolean; data?: Record<string, unknown> }; }
interface ThinkingContent { type: 'thinking'; text: string; }

type ContentItem = TextContent | ImageContent | AudioContent | VideoContent | FileContent | ToolCallContent | ToolResultContent | ThinkingContent;

interface Message {
  id: string;
  chatId: string;
  senderId: string;
  senderUsername: string;
  senderAvatar?: string;
  senderType: 'human' | 'agent';
  content?: string;
  contentItems?: ContentItem[];
  role: 'user' | 'assistant' | 'system' | 'tool';
  senderKind: 'human' | 'agent';
  mediaUrl?: string;
  mediaType?: string;
  replyToId?: string;
  replyTo?: { id: string; content: string; senderUsername: string };
  toolCallId?: string;
  toolName?: string;
  toolArguments?: string;
  uampEventId?: string;
  metadata?: Record<string, unknown>;
  isEdited?: boolean;
  isDeleted?: boolean;
  createdAt: string;
}

type ChatSource =
  | 'whatsapp'
  | 'messenger'
  | 'instagram'
  | 'telegram'
  | 'discord'
  | 'slack'
  | 'twilio'
  | 'x'
  | 'sendgrid'
  | 'google-chat'
  | 'linkedin'
  | 'bluesky'
  | 'reddit';

interface Chat {
  id: string;
  type: 'dm' | 'group';
  name?: string;
  avatarUrl?: string;
  createdById?: string;
  /**
   * Persisted on `chats.metadata` by the bridge framework. Only `source`
   * is consumed in the UI today (header chip), but the full bag is passed
   * through so future fields don't require another schema bump here.
   */
  metadata?: Record<string, unknown>;
  participants: Array<{
    userId: string;
    username: string;
    displayName: string;
    avatarUrl?: string;
    accountType: 'human' | 'agent';
    isOnline?: boolean;
  }>;
}

// Same source -> chip mapping as `chat-list.tsx`. Kept inline so this file
// stays self-contained; the badge is small enough that DRYing it would cost
// more than it saves.
const HEADER_SOURCE_BADGE: Record<ChatSource, { slug: string; label: string; tint: string }> = {
  whatsapp:  { slug: 'whatsapp',  label: 'WhatsApp',  tint: 'text-emerald-500' },
  messenger: { slug: 'facebook',  label: 'Messenger', tint: 'text-blue-500' },
  instagram: { slug: 'instagram', label: 'Instagram', tint: 'text-pink-500' },
  telegram:  { slug: 'telegram',  label: 'Telegram',  tint: 'text-sky-500' },
  discord:   { slug: 'discord',   label: 'Discord',   tint: 'text-indigo-500' },
  slack:     { slug: 'slack',     label: 'Slack',     tint: 'text-fuchsia-500' },
  twilio:    { slug: 'twilio',    label: 'SMS',       tint: 'text-red-500' },
  x:         { slug: 'x',         label: 'X',         tint: 'text-foreground' },
  sendgrid:  { slug: 'sendgrid',  label: 'Email',     tint: 'text-amber-500' },
  'google-chat': { slug: 'google-chat', label: 'Google Chat', tint: 'text-emerald-500' },
  linkedin:  { slug: 'linkedin',  label: 'LinkedIn',  tint: 'text-blue-600' },
  bluesky:   { slug: 'bluesky',   label: 'Bluesky',   tint: 'text-sky-500' },
  reddit:    { slug: 'reddit',    label: 'Reddit',    tint: 'text-orange-500' },
};
const ALLOWED_CHAT_SOURCES = new Set<string>(Object.keys(HEADER_SOURCE_BADGE));

/**
 * Providers whose chats expose the "send to platform" composer toggle.
 * Mirrors `OWNER_OUTBOUND_PROVIDERS` in `lib/messaging/owner-outbound-bridge.ts`
 * — duplicated here so this client component doesn't pull the server
 * module's DB / messaging deps into the bundle.
 */
const OWNER_OUTBOUND_PROVIDERS = new Set<string>([
  'telegram',
  'slack',
  'discord',
  'whatsapp',
  'messenger',
  'instagram',
  'twilio',
  'x',
  'sendgrid',
]);

/**
 * Initial-history fetch schedule. One watchdog bound per attempt, so the
 * ladder length IS the attempt budget; the delay between attempts backs
 * off. Reasoning lives on the watchdog block inside `fetchChat`; the short
 * version: 2026-09-06, two browser runs on the local cluster had
 * `GET /api/messages/{chatId}` answering in 30 to 60s under load, a flat
 * 15s bound aborted every attempt, and the person had no composer.
 */
const HISTORY_FETCH_TIMEOUTS_MS = [15_000, 30_000, 60_000];
const HISTORY_FETCH_RETRY_DELAYS_MS = [1_000, 2_000];
/**
 * How long the full-view spinner may hide the composer while the FIRST
 * history attempt is in flight. Independent of the watchdog on purpose:
 * revealing the composer is about the person being able to act, aborting
 * is about a hung socket, and tying them together is what produced the
 * lock-out. Sub-second loads (the healthy path) never reach this.
 */
const HISTORY_REVEAL_MS = 5_000;

function ChatSourceBadge({ source }: { source?: string }) {
  if (!source || !ALLOWED_CHAT_SOURCES.has(source)) return null;
  const cfg = HEADER_SOURCE_BADGE[source as ChatSource];
  return (
    <span
      title={`From ${cfg.label}`}
      aria-label={`From ${cfg.label}`}
      className={cn(
        'shrink-0 inline-flex items-center justify-center rounded-sm px-1 py-0.5 bg-muted/60',
        cfg.tint,
      )}
    >
      <ProviderIcon slug={cfg.slug} size={12} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Reducer: single state atom for all message-related data
// ---------------------------------------------------------------------------

type ProgressSegment = { kind: 'append' | 'replace' | 'thinking'; text: string };

type StreamingItem =
  | { type: 'text'; text: string }
  | { type: 'tool_call'; id: string; name: string; arguments: string; result?: string; data?: Record<string, unknown>; progressSegments?: ProgressSegment[]; awaitingApproval?: boolean }
  | { type: 'file'; content_id: string; filename: string; mime_type: string; metadata?: Record<string, unknown> }
  | { type: 'thinking'; text: string }
  | { type: 'system_notice'; text: string };

interface StreamOverlay {
  messageId: string;
  agentId: string;
  items: StreamingItem[];
}

interface ChatState {
  messages: Message[];
  overlays: Map<string, StreamOverlay>; // keyed by responseId (= uampEventId)
}

type ChatAction =
  | { type: 'MESSAGES_LOADED'; messages: Message[]; isInitial: boolean }
  | { type: 'MESSAGES_PREPENDED'; messages: Message[] }
  | { type: 'OPTIMISTIC_SEND'; message: Message }
  | { type: 'OPTIMISTIC_CONFIRM'; tempId: string; message: Message }
  | { type: 'OPTIMISTIC_FAIL'; tempId: string }
  | { type: 'WS_MESSAGE'; message: Message }
  | { type: 'OVERLAY_DELTA'; responseId: string; items: StreamingItem[]; messageId: string; agentId: string }
  | { type: 'OVERLAY_CLEAR'; responseId: string }
  | { type: 'MESSAGE_UPDATED'; message: Message }
  | { type: 'MESSAGE_DELETED'; messageId: string }
  | { type: 'MESSAGE_EDITED'; messageId: string; content: string }
  | { type: 'STREAM_COMPLETE'; responseId: string }
  | { type: 'CANCEL' };

const initialChatState: ChatState = {
  messages: [],
  overlays: new Map(),
};

/**
 * Convert in-memory streaming overlay items into the persisted ContentItem[]
 * shape and merge them into an existing contentItems array, preferring the
 * existing entry when ids collide. Used by the STREAM_COMPLETE race-guard so
 * a late response.done doesn't drop live tool-call icons after a cancel.
 */
function mergeOverlayIntoContentItems(
  existing: ContentItem[] | undefined,
  overlayItems: StreamingItem[],
): ContentItem[] {
  const merged: ContentItem[] = existing ? [...existing] : [];
  const existingToolIds = new Set(
    merged.filter(i => i.type === 'tool_call').map(i => (i as ToolCallContent).tool_call.id),
  );
  const existingResultIds = new Set(
    merged.filter(i => i.type === 'tool_result').map(i => (i as ToolResultContent).tool_result.call_id),
  );
  for (const item of overlayItems) {
    if (item.type === 'tool_call') {
      if (!existingToolIds.has(item.id)) {
        merged.push({ type: 'tool_call', tool_call: { id: item.id, name: item.name, arguments: item.arguments } });
        existingToolIds.add(item.id);
      }
      if (item.result !== undefined && !existingResultIds.has(item.id)) {
        merged.push({ type: 'tool_result', tool_result: { call_id: item.id, result: item.result, data: item.data } });
        existingResultIds.add(item.id);
      }
    } else if (item.type === 'thinking') {
      merged.push({ type: 'thinking', text: item.text });
    } else if (item.type === 'file') {
      merged.push({ type: 'file', file: '', filename: item.filename, mime_type: item.mime_type, content_id: item.content_id, metadata: item.metadata });
    } else if (item.type === 'text') {
      // text is captured separately on message.content; avoid double-rendering
    }
  }
  return merged;
}

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'MESSAGES_LOADED': {
      if (action.isInitial) {
        if (STREAM_DBG) {
          const lastAsst = [...action.messages].reverse().find(m => m.role === 'assistant');
          sdbg(`MESSAGES_LOADED initial: count=${action.messages.length} lastAsst.id=${lastAsst?.id ?? 'none'} lastAsst.contentLen=${(lastAsst?.content || '').length} lastAsst.status=${lastAsst?.metadata?.status ?? 'none'}`);
        }
        return { ...state, messages: action.messages };
      }
      const serverIds = new Set(action.messages.map(m => m.id));
      const prevReal = state.messages.filter(m => !m.id.startsWith('temp-'));
      const optimistic = state.messages.filter(m => m.id.startsWith('temp-'));

      const hasMetadataChange = prevReal.some(m => {
        const server = action.messages.find(s => s.id === m.id);
        if (!server) return false;
        return server.metadata?.status !== m.metadata?.status
          || server.content !== m.content
          || (server.contentItems?.length ?? 0) !== (m.contentItems?.length ?? 0);
      });

      if (STREAM_DBG) {
        const lastAsstServer = [...action.messages].reverse().find(m => m.role === 'assistant');
        const lastAsstPrev = [...prevReal].reverse().find(m => m.role === 'assistant');
        sdbg(`MESSAGES_LOADED non-initial: serverCount=${action.messages.length} prevRealCount=${prevReal.length} optimistic=${optimistic.length} hasMetadataChange=${hasMetadataChange} server.lastAsst.id=${lastAsstServer?.id ?? 'none'} server.lastAsst.contentLen=${(lastAsstServer?.content || '').length} server.lastAsst.status=${lastAsstServer?.metadata?.status ?? 'none'} prev.lastAsst.id=${lastAsstPrev?.id ?? 'none'} prev.lastAsst.contentLen=${(lastAsstPrev?.content || '').length} prev.lastAsst.status=${lastAsstPrev?.metadata?.status ?? 'none'}`);
      }

      if (
        prevReal.length === serverIds.size &&
        prevReal.every(m => serverIds.has(m.id)) &&
        optimistic.length === 0 &&
        !hasMetadataChange
      ) {
        sdbg('MESSAGES_LOADED non-initial: short-circuit no-op (server matches state)');
        return state;
      }

      // Clear overlays for messages that are no longer streaming
      let newOverlays = state.overlays;
      for (const msg of action.messages) {
        const status = msg.metadata?.status as string | undefined;
        if (status === 'complete' || status === 'error' || (!status && msg.content)) {
          const eventId = msg.uampEventId;
          if (eventId && newOverlays.has(eventId)) {
            if (newOverlays === state.overlays) newOverlays = new Map(state.overlays);
            newOverlays.delete(eventId);
          } else {
            for (const [key, overlay] of newOverlays) {
              if (overlay.messageId === msg.id) {
                if (newOverlays === state.overlays) newOverlays = new Map(state.overlays);
                newOverlays.delete(key);
                break;
              }
            }
          }
        }
      }

      // Preserve locally-set terminal statuses (and the content we streamed
      // into them) so a stale server refetch can't revert the row back to the
      // empty placeholder. We hit this when the post-`response.done` safety-net
      // fetch lands before the DB write from `finalizeMessage` is visible to a
      // subsequent SELECT (replication lag, prepared-statement pipelining, etc.):
      // the GET returns `content='' status='streaming'`, which would otherwise
      // wipe the message we just finished streaming on the client.
      const localFinalMap = new Map<string, Message>();
      for (const m of state.messages) {
        const s = m.metadata?.status as string | undefined;
        if (s === 'complete' || s === 'cancelled') localFinalMap.set(m.id, m);
      }
      const mergedMessages = localFinalMap.size > 0
        ? action.messages.map(m => {
            const localFinal = localFinalMap.get(m.id);
            if (!localFinal) return m;
            const serverStatus = m.metadata?.status as string | undefined;
            const serverContentLen = typeof m.content === 'string' ? m.content.length : 0;
            const serverItemsLen = m.contentItems?.length ?? 0;
            const localContentLen = typeof localFinal.content === 'string' ? localFinal.content.length : 0;
            const localItemsLen = localFinal.contentItems?.length ?? 0;
            // If server still says streaming OR returns an empty body but we
            // locally have terminal content, prefer the local content+status.
            const serverLooksStale = serverStatus === 'streaming'
              || (serverContentLen === 0 && serverItemsLen === 0 && (localContentLen > 0 || localItemsLen > 0));
            if (serverLooksStale) {
              if (STREAM_DBG) {
                sdbg(`MESSAGES_LOADED preserve-local: id=${m.id} serverStatus=${serverStatus ?? 'none'} serverContentLen=${serverContentLen} serverItemsLen=${serverItemsLen} localStatus=${localFinal.metadata?.status ?? 'none'} localContentLen=${localContentLen} localItemsLen=${localItemsLen}`);
              }
              return {
                ...m,
                content: localFinal.content,
                contentItems: localFinal.contentItems,
                mediaUrl: m.mediaUrl ?? localFinal.mediaUrl,
                mediaType: m.mediaType ?? localFinal.mediaType,
                metadata: {
                  ...m.metadata,
                  ...localFinal.metadata,
                  status: localFinal.metadata?.status,
                },
              } as Message;
            }
            return m;
          })
        : action.messages;

      const serverLatestTs = action.messages.length > 0
        ? new Date(action.messages[action.messages.length - 1].createdAt).getTime()
        : 0;
      const trailing = state.messages.filter(m =>
        !serverIds.has(m.id) &&
        (m.id.startsWith('temp-') || new Date(m.createdAt).getTime() >= serverLatestTs)
      );
      if (trailing.length === 0) {
        if (STREAM_DBG) {
          const lastAsst = [...mergedMessages].reverse().find(m => m.role === 'assistant');
          sdbg(`MESSAGES_LOADED merged (no trailing): count=${mergedMessages.length} overlaysCleared=${newOverlays !== state.overlays} lastAsst.id=${lastAsst?.id ?? 'none'} lastAsst.contentLen=${(lastAsst?.content || '').length} lastAsst.status=${lastAsst?.metadata?.status ?? 'none'}`);
        }
        return { ...state, messages: mergedMessages, overlays: newOverlays };
      }
      const mergedIds = new Set(mergedMessages.map(m => m.id));
      const safeTrailing = trailing.filter(m => !mergedIds.has(m.id));
      const finalMsgs = safeTrailing.length > 0 ? [...mergedMessages, ...safeTrailing] : mergedMessages;
      if (STREAM_DBG) {
        const lastAsst = [...finalMsgs].reverse().find(m => m.role === 'assistant');
        sdbg(`MESSAGES_LOADED merged (with trailing): count=${finalMsgs.length} trailing=${safeTrailing.length} overlaysCleared=${newOverlays !== state.overlays} lastAsst.id=${lastAsst?.id ?? 'none'} lastAsst.contentLen=${(lastAsst?.content || '').length} lastAsst.status=${lastAsst?.metadata?.status ?? 'none'}`);
      }
      return { ...state, messages: finalMsgs, overlays: newOverlays };
    }

    case 'MESSAGES_PREPENDED':
      return { ...state, messages: [...action.messages, ...state.messages] };

    case 'OPTIMISTIC_SEND':
      return { ...state, messages: [...state.messages, action.message] };

    case 'OPTIMISTIC_CONFIRM': {
      const idx = state.messages.findIndex(m => m.id === action.tempId);
      if (idx < 0) return state;
      const updated = [...state.messages];
      updated[idx] = action.message;
      return { ...state, messages: updated };
    }

    case 'OPTIMISTIC_FAIL': {
      const failIdx = state.messages.findIndex(m => m.id === action.tempId);
      if (failIdx < 0) return state;
      const failUpdated = [...state.messages];
      failUpdated[failIdx] = { ...failUpdated[failIdx], metadata: { ...failUpdated[failIdx].metadata, failed: true } };
      return { ...state, messages: failUpdated };
    }

    case 'WS_MESSAGE': {
      if (state.messages.some(m => m.id === action.message.id)) {
        return state;
      }
      const optIdx = state.messages.findIndex(
        m => m.id.startsWith('temp-') && m.senderId === action.message.senderId
      );
      let newMessages: Message[];
      if (optIdx >= 0) {
        newMessages = [...state.messages];
        newMessages[optIdx] = action.message;
      } else {
        newMessages = [...state.messages, action.message];
      }
      return { ...state, messages: newMessages };
    }

    case 'OVERLAY_DELTA': {
      const newOverlays = new Map(state.overlays);
      newOverlays.set(action.responseId, {
        messageId: action.messageId,
        agentId: action.agentId,
        items: action.items,
      });
      return { ...state, overlays: newOverlays };
    }

    case 'OVERLAY_CLEAR': {
      if (!state.overlays.has(action.responseId)) return state;
      const newOverlays = new Map(state.overlays);
      newOverlays.delete(action.responseId);
      return { ...state, overlays: newOverlays };
    }

    case 'STREAM_COMPLETE': {
      const overlay = state.overlays.get(action.responseId);
      const messageId = overlay?.messageId;
      const overlayItemsCount = overlay?.items.length ?? 0;
      const targetIdx = messageId ? state.messages.findIndex(m => m.id === messageId) : -1;
      const target = targetIdx >= 0 ? state.messages[targetIdx] : undefined;
      sdbg(`STREAM_COMPLETE: responseId=${action.responseId} hasOverlay=${!!overlay} overlayItems=${overlayItemsCount} messageId=${messageId ?? 'none'} targetIdx=${targetIdx} target.status=${target?.metadata?.status ?? 'none'} target.contentLen=${(target?.content || '').length} target.contentItems=${target?.contentItems?.length ?? 0} totalMessages=${state.messages.length}`);
      // Race/legacy guard: if the targeted message was already marked cancelled
      // (because Stop fired and markChatStreamingPlaceholdersCancelled landed
      // first, but a stale response.done snuck through anyway), freeze the
      // overlay items into message.contentItems so the rich tool-strip stays
      // visible after we delete the overlay. The DB write may have already
      // captured the same items (router now flushes per tool_call/tool_result),
      // but merging by id is idempotent.
      if (messageId) {
        const idx = state.messages.findIndex(m => m.id === messageId);
        const targetStatus = idx >= 0 ? (state.messages[idx].metadata?.status as string | undefined) : undefined;
        if (idx >= 0 && targetStatus === 'cancelled' && overlay) {
          const merged = mergeOverlayIntoContentItems(state.messages[idx].contentItems, overlay.items);
          const newOverlays = new Map(state.overlays);
          newOverlays.delete(action.responseId);
          const newMessages = [...state.messages];
          newMessages[idx] = {
            ...newMessages[idx],
            contentItems: merged,
            metadata: { ...newMessages[idx].metadata, partial: true },
          };
          sdbg(`STREAM_COMPLETE: cancelled-merge path idx=${idx}`);
          return { ...state, messages: newMessages, overlays: newOverlays };
        }
      }
      let newOverlays = state.overlays;
      if (state.overlays.has(action.responseId)) {
        newOverlays = new Map(state.overlays);
        newOverlays.delete(action.responseId);
      }
      // Preferred path: flip the specific message
      if (messageId) {
        const idx = state.messages.findIndex(m => m.id === messageId);
        if (idx >= 0 && state.messages[idx].metadata?.status === 'streaming') {
          const newMessages = [...state.messages];
          newMessages[idx] = { ...newMessages[idx], metadata: { ...newMessages[idx].metadata, status: 'complete' } };
          sdbg(`STREAM_COMPLETE: flipped messageId=${messageId} streaming→complete contentLen=${(newMessages[idx].content || '').length} contentItems=${newMessages[idx].contentItems?.length ?? 0}`);
          return { ...state, messages: newMessages, overlays: newOverlays };
        }
        sdbg(`STREAM_COMPLETE: did NOT flip messageId=${messageId} (idx=${idx} status=${state.messages[idx]?.metadata?.status ?? 'none'})`);
      }
      // Safety net: flip any remaining streaming assistant messages to complete.
      // Handles race where MESSAGE_UPDATED cleared overlay but didn't flip status.
      const safe = state.messages.map(m =>
        m.metadata?.status === 'streaming' ? { ...m, metadata: { ...m.metadata, status: 'complete' } } : m
      );
      const changed = safe.some((m, i) => m !== state.messages[i]);
      sdbg(`STREAM_COMPLETE: safety-net changed=${changed}`);
      return changed
        ? { ...state, messages: safe, overlays: newOverlays }
        : (newOverlays === state.overlays ? state : { ...state, overlays: newOverlays });
    }

    case 'CANCEL': {
      const hasStreaming = state.messages.some(m => m.metadata?.status === 'streaming');
      if (!hasStreaming) return state;
      const cancelledMessages = state.messages.map(m =>
        m.metadata?.status === 'streaming'
          ? { ...m, metadata: { ...m.metadata, status: 'cancelled' } }
          : m,
      );
      return { ...state, messages: cancelledMessages };
    }

    case 'MESSAGE_UPDATED': {
      const idx = state.messages.findIndex(m => m.id === action.message.id);
      const incomingStatus = action.message.metadata?.status as string | undefined;
      const incomingContentLen = (action.message.content || '').length;
      const incomingContentItems = action.message.contentItems?.length ?? 0;
      sdbg(`MESSAGE_UPDATED: messageId=${action.message.id} idx=${idx} status=${incomingStatus ?? 'none'} contentLen=${incomingContentLen} contentItems=${incomingContentItems} uampEventId=${action.message.uampEventId ?? 'none'} totalMessages=${state.messages.length}`);
      let newMessages = state.messages;
      if (idx >= 0) {
        newMessages = [...state.messages];
        newMessages[idx] = { ...action.message, isEdited: action.message.isEdited || state.messages[idx].isEdited };
      }
      const status = action.message.metadata?.status as string | undefined;
      if (status === 'complete' || status === 'error') {
        const eventId = action.message.uampEventId;
        let overlayKey: string | undefined;
        if (eventId && state.overlays.has(eventId)) {
          overlayKey = eventId;
        } else {
          for (const [key, overlay] of state.overlays) {
            if (overlay.messageId === action.message.id) {
              overlayKey = key;
              break;
            }
          }
        }
        if (overlayKey) {
          const newOverlays = new Map(state.overlays);
          newOverlays.delete(overlayKey);
          sdbg(`MESSAGE_UPDATED: cleared overlay key=${overlayKey} (status=${status})`);
          return { ...state, messages: newMessages, overlays: newOverlays };
        }
        sdbg(`MESSAGE_UPDATED: status=${status} but NO overlay matched (eventId=${eventId ?? 'none'})`);
      }
      if (newMessages === state.messages) sdbg(`MESSAGE_UPDATED: no-op (idx<0 and no overlay clear)`);
      return newMessages === state.messages ? state : { ...state, messages: newMessages };
    }

    case 'MESSAGE_DELETED': {
      const idx = state.messages.findIndex(m => m.id === action.messageId);
      if (idx < 0) return state;
      const updated = [...state.messages];
      updated[idx] = { ...updated[idx], isDeleted: true, content: undefined, contentItems: undefined, mediaUrl: undefined };
      return { ...state, messages: updated };
    }

    case 'MESSAGE_EDITED': {
      const idx = state.messages.findIndex(m => m.id === action.messageId);
      if (idx < 0) return state;
      const updated = [...state.messages];
      const existingNonText = (updated[idx].contentItems || []).filter(i => i.type !== 'text');
      updated[idx] = {
        ...updated[idx],
        content: action.content,
        contentItems: [{ type: 'text' as const, text: action.content }, ...existingNonText],
        isEdited: true,
      };
      return { ...state, messages: updated };
    }

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Grouping (pure, called inside useMemo)
// ---------------------------------------------------------------------------

function shouldGroupMessages(msg1: Message, msg2: Message): boolean {
  if (msg1.senderId !== msg2.senderId) return false;
  const time1 = new Date(msg1.createdAt).getTime();
  const time2 = new Date(msg2.createdAt).getTime();
  return Math.abs(time2 - time1) < 2 * 60 * 1000;
}

interface MessageGroup {
  senderId: string;
  senderUsername: string;
  senderAvatar?: string;
  senderType: 'human' | 'agent';
  messages: Message[];
  firstTimestamp: string;
  lastTimestamp: string;
  groupKey: string;
}

function groupMessages(messages: Message[]): MessageGroup[] {
  const seen = new Set<string>();
  const groups: MessageGroup[] = [];
  for (const message of messages) {
    if (seen.has(message.id)) continue;
    seen.add(message.id);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && shouldGroupMessages(lastGroup.messages[lastGroup.messages.length - 1], message)) {
      lastGroup.messages.push(message);
      lastGroup.lastTimestamp = message.createdAt;
    } else {
      groups.push({
        senderId: message.senderId,
        senderUsername: message.senderUsername,
        senderAvatar: message.senderAvatar,
        senderType: message.senderType,
        messages: [message],
        firstTimestamp: message.createdAt,
        lastTimestamp: message.createdAt,
        groupKey: message.id,
      });
    }
  }
  return groups;
}

// ---------------------------------------------------------------------------
// MessageActions (unchanged)
// ---------------------------------------------------------------------------

function MessageActions({ messageId, isOwn, canEdit, onEdit, onDelete }: {
  messageId: string;
  isOwn: boolean;
  canEdit: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const router = useRouter();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex md:opacity-0 md:group-hover:opacity-100 transition-opacity">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className="p-0.5 rounded transition-colors text-muted-foreground hover:text-foreground"
      >
        <MoreHorizontal className="h-3 w-3" />
      </button>
      {open && (
        <div className={cn(
          "absolute bottom-full mb-1 z-50 bg-popover border border-border rounded-md shadow-lg py-1 min-w-[140px]",
          isOwn ? 'right-0' : 'left-0'
        )}>
          {isOwn && canEdit && (
            <button
              className="w-full px-3 py-1.5 text-xs text-left flex items-center gap-2 hover:bg-accent transition-colors"
              onClick={() => { onEdit?.(); setOpen(false); }}
            >
              <Pencil className="h-3 w-3" />
              {t('messages.edit')}
            </button>
          )}
          {onDelete && (
            <button
              className="w-full px-3 py-1.5 text-xs text-left flex items-center gap-2 hover:bg-accent transition-colors text-destructive"
              onClick={() => { onDelete(); setOpen(false); }}
            >
              <Trash2 className="h-3 w-3" />
              {t('messages.delete')}
            </button>
          )}
          {!isOwn && (
            <button
              className="w-full px-3 py-1.5 text-xs text-left flex items-center gap-2 hover:bg-accent transition-colors"
              onClick={() => {
                router.push(`/create?tab=post&from_message=${messageId}`);
                setOpen(false);
              }}
            >
              <PenSquare className="h-3 w-3" />
              {t('messages.createPostFromMsg')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Memoized persisted message content (prevents re-render on overlay changes)
// ---------------------------------------------------------------------------

interface PersistedContentHandlers {
  openLightbox: (items: LightboxItem[], startIndex?: number) => void;
  /**
   * Wired only when the chat is hosted inside a workspace canvas
   * (`hostingAgentItemId` is set). Routes the media as a content
   * widget to be spawned next to the originating agent. Standalone
   * chat surfaces leave this undefined so the "add to canvas" button
   * in `ImagePreview` / `VideoPreview` stays hidden.
   */
  addMediaToCanvas?: (
    contentId: string,
    meta?: { displayName?: string; mimeType?: string },
  ) => void;
}

interface PersistedMessageContentProps {
  message: Message;
  isOwn: boolean;
  handlers: React.RefObject<PersistedContentHandlers | null>;
}

const PersistedMessageContent = memo(function PersistedMessageContent({ message, isOwn, handlers }: PersistedMessageContentProps) {
  if (message.contentItems && message.contentItems.length > 0) {
    const mediaUuids = new Set<string>();
    for (const ci of message.contentItems) {
      let url: string | undefined;
      if (ci.type === 'image' && 'image' in ci) url = typeof ci.image === 'string' ? ci.image : ci.image.url;
      else if (ci.type === 'video' && 'video' in ci) url = typeof ci.video === 'string' ? ci.video : ci.video.url;
      else if (ci.type === 'audio' && 'audio' in ci) url = typeof ci.audio === 'string' ? ci.audio : ci.audio.url;
      if (url) { const m = url.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i); if (m) mediaUuids.add(m[0]); }
    }
    // Group consecutive tool_calls into runs that render as a ToolCallStrip.
    // Messaging-platform send tools (telegram_send_text, etc.) are pulled
    // out of the run and emitted as their own top-level "messaging_bubble"
    // group so the bubble shows directly in the message body — not hidden
    // behind the auto-collapsing tool-call accordion.
    type Group =
      | { kind: 'tool_run'; key: string; items: ToolStripItem[] }
      | { kind: 'messaging_bubble'; key: string; toolName: string; args?: string; result?: string; isError?: boolean }
      | { kind: 'item'; key: string; idx: number; item: ContentItem };
    const groups: Group[] = [];
    let runStart: ToolStripItem[] | null = null;
    let runKey = '';
    const flushRun = () => {
      if (runStart) {
        groups.push({ kind: 'tool_run', key: runKey, items: runStart });
        runStart = null;
      }
    };
    for (let i = 0; i < message.contentItems.length; i++) {
      const it = message.contentItems[i];
      if (it.type === 'tool_result') continue;
      if (it.type === 'tool_call' && 'tool_call' in it) {
        if (it.tool_call.name === 'present' || it.tool_call.name.startsWith('present.')) continue;
        const resultItem = message.contentItems.find(
          (r) => r.type === 'tool_result' && 'tool_result' in r && r.tool_result.call_id === it.tool_call.id,
        ) as ToolResultContent | undefined;
        if (isMessagingSendTool(it.tool_call.name)) {
          flushRun();
          groups.push({
            kind: 'messaging_bubble',
            key: `bubble-${it.tool_call.id || i}`,
            toolName: it.tool_call.name,
            args: it.tool_call.arguments,
            result: resultItem?.tool_result.result,
            isError: resultItem?.tool_result.is_error,
          });
          continue;
        }
        const stripItem: ToolStripItem = {
          id: it.tool_call.id || `tc-${i}`,
          kind: 'tool',
          name: it.tool_call.name,
          args: it.tool_call.arguments,
          result: resultItem?.tool_result.result,
          isError: resultItem?.tool_result.is_error,
          data: resultItem?.tool_result.data,
        };
        if (runStart) {
          runStart.push(stripItem);
        } else {
          runStart = [stripItem];
          runKey = `run-${stripItem.id}`;
        }
        continue;
      }
      // Fold thinking blocks into the current/next tool run so the brain icon
      // appears inline with the tool chips instead of breaking the row.
      if (it.type === 'thinking' && 'text' in it) {
        const stripItem: ToolStripItem = {
          id: `think-${i}`,
          kind: 'thinking',
          name: 'thinking',
          text: it.text,
        };
        if (runStart) {
          runStart.push(stripItem);
        } else {
          runStart = [stripItem];
          runKey = `run-think-${i}`;
        }
        continue;
      }
      // Skip empty text segments entirely — they should not break a run of
      // consecutive tool/thinking chips into a second row.
      if (it.type === 'text' && 'text' in it && !it.text.trim()) continue;
      flushRun();
      groups.push({ kind: 'item', key: `${it.type}-${i}`, idx: i, item: it });
    }
    flushRun();

    return (
      <SmoothShrink>
        <div className="space-y-1 min-w-0 overflow-hidden">
        {groups.map((group): ReactNode => {
          if (group.kind === 'tool_run') {
            return <ToolCallStrip key={group.key} items={group.items} />;
          }
          if (group.kind === 'messaging_bubble') {
            const props = getMessagingSendBubbleProps(
              group.toolName,
              group.args,
              group.result,
              group.isError,
              false,
            );
            return props ? <MessagingSendBubble key={group.key} {...props} /> : null;
          }
          const item = group.item;
          const idx = group.idx;
          if (item.type === 'thinking') {
            return <ThinkingBlock key={`thinking-${idx}`} content={item.text} />;
          }

          const stableKey =
            (item.type === 'image' && 'image' in item) ? (typeof item.image === 'string' ? item.image : item.image.url) :
            (item.type === 'video' && 'video' in item) ? (typeof item.video === 'string' ? item.video : item.video.url) :
            (item.type === 'audio' && 'audio' in item) ? (typeof item.audio === 'string' ? item.audio : item.audio.url) :
            (item.type === 'file') ? ((item as FileContent).content_id || (item as FileContent).file?.toString() || `file-${idx}`) :
            `${item.type}-${idx}`;

          return (
            <div key={stableKey}>
              {item.type === 'text' && 'text' in item && (
                <div className={cn("text-sm break-words prose prose-sm max-w-none min-w-0 overflow-hidden dark:prose-invert", isOwn && "text-right")}><Markdown suppressLinkPreviews suppressContentUrls={mediaUuids.size > 0 ? mediaUuids : undefined}>{stripContentLabels(item.text)}</Markdown></div>
              )}
              {item.type === 'image' && 'image' in item && (() => { const imgUrl = typeof item.image === 'string' ? item.image : item.image.url; const contentId = item.content_id || imgUrl.match(/\/api\/content\/([0-9a-f-]{36})/i)?.[1]; const addToCanvas = contentId && handlers.current?.addMediaToCanvas ? () => handlers.current?.addMediaToCanvas?.(contentId, { displayName: item.alt_text, mimeType: 'image/*' }) : undefined; return <><ImagePreview src={imgUrl} alt={item.alt_text} className="max-w-sm" onClick={() => handlers.current?.openLightbox([{ url: imgUrl, type: 'image', filename: item.alt_text, contentId }])} onAddToCanvas={addToCanvas} /><LinkBar url={imgUrl} internal /></>; })()}
              {item.type === 'audio' && 'audio' in item && (() => { const audioSrc = typeof item.audio === 'string' ? item.audio : item.audio.url; const audioContentId = item.content_id || audioSrc.match(/\/api\/content\/([0-9a-f-]{36})/i)?.[1]; const audioMime = item.format ? (item.format.includes('/') ? item.format : `audio/${item.format}`) : undefined; return <><div className="max-w-sm"><AudioPlayer src={audioSrc} compact onMediaClick={() => handlers.current?.openLightbox([{ url: audioSrc, type: 'audio', contentId: audioContentId, mimeType: audioMime }])} /></div><LinkBar url={audioSrc} internal /></>; })()}
              {item.type === 'video' && 'video' in item && (() => { const vidUrl = typeof item.video === 'string' ? item.video : item.video.url; const contentId = item.content_id || vidUrl.match(/\/api\/content\/([0-9a-f-]{36})/i)?.[1]; const addToCanvas = contentId && handlers.current?.addMediaToCanvas ? () => handlers.current?.addMediaToCanvas?.(contentId, { mimeType: 'video/*' }) : undefined; return <><VideoPreview src={vidUrl} className="max-w-sm" onClick={() => handlers.current?.openLightbox([{ url: vidUrl, type: 'video', contentId }])} onAddToCanvas={addToCanvas} /><LinkBar url={vidUrl} internal /></>; })()}
              {item.type === 'file' && (() => {
                const fileItem = item as FileContent;
                const fileUrl = fileItem.file ? (typeof fileItem.file === 'string' ? fileItem.file : fileItem.file.url) : (fileItem.content_id ? `/api/content/${fileItem.content_id}` : '');
                const is3D = /^model\/(gltf-binary|gltf\+json|vnd\.usdz\+zip)/.test(fileItem.mime_type || '') || /\.(glb|gltf|usdz)$/i.test(fileItem.filename || '');
                if (is3D) { const cid = fileItem.content_id || fileUrl.match(/\/api\/content\/([0-9a-f-]{36})/i)?.[1]; return <><Model3DPreview src={fileUrl} className="max-w-sm w-full aspect-[4/3]" onClick={() => handlers.current?.openLightbox([{ url: fileUrl, type: 'model3d', filename: fileItem.filename, mimeType: fileItem.mime_type, contentId: cid }])} /><LinkBar url={fileUrl} internal /></>; }
                const contentId = fileItem.content_id || (fileUrl.includes('/api/content/') ? fileUrl.split('/api/content/')[1]?.slice(0, 36) : undefined);
                if (contentId) {
                  const meta = fileItem.metadata as Record<string, unknown> | undefined;
                  return <DocumentChip contentId={contentId} filename={fileItem.filename} mimeType={fileItem.mime_type} command={meta?.command as string | undefined} diff={meta ? { old_str: meta.old_str as string | undefined, new_str: meta.new_str as string | undefined, insert_line: meta.insert_line as number | undefined } : undefined} />;
                }
                if (!fileUrl) return null;
                return <><DocumentPreview url={fileUrl} filename={fileItem.filename} mimeType={fileItem.mime_type} /><LinkBar url={fileUrl} internal /></>;
              })()}
            </div>
          );
        })}
        </div>
      </SmoothShrink>
    );
  }

  if (message.content && !/^tool_code\s/i.test(message.content.trimStart())) {
    return (
      <div className={cn("text-sm break-words prose prose-sm max-w-none min-w-0 overflow-hidden dark:prose-invert", isOwn && "text-left")}><Markdown suppressLinkPreviews>{stripContentLabels(message.content)}</Markdown></div>
    );
  }

  return null;
}, (prev, next) =>
  prev.message.id === next.message.id &&
  prev.message.content === next.message.content &&
  prev.message.contentItems === next.message.contentItems &&
  prev.isOwn === next.isOwn
);

// ---------------------------------------------------------------------------
// ChatView component
// ---------------------------------------------------------------------------

export interface ChatViewProps {
  chatId?: string;
  draft?: {
    participantUsername: string;
    prefillText: string;
    /**
     * Hint that the draft participant is an agent (the embedded agent
     * widget always is). Lets draft mode paint the agent greeting
     * optimistically instead of waiting on the `/api/users` profile
     * round-trip to learn the account type.
     */
    participantIsAgent?: boolean;
  };
  onBack?: () => void;
  onChatCreated?: (chatId: string) => void;
  embedded?: boolean;
  prefill?: string;
  /**
   * Hide the built-in chat header (avatar / name / Plus / X / Pencil row).
   * Useful when the embedding shell already provides identity affordances —
   * e.g. the workspace AgentNode shows the agent avatar as a corner badge
   * and exposes its own action dot strip, so the duplicate header would be
   * visual noise.
   */
  hideHeader?: boolean;
  /**
   * Optional host-side intercept for "open this content" actions
   * (e.g. clicking a `<DocumentChip>` attached to an assistant
   * message). When the host returns truthy the default
   * `router.push('/content/<id>')` is suppressed — letting an
   * embedder (e.g. an `AgentNode` on a workspace canvas) spawn a
   * sibling content widget instead of navigating away.
   */
  onOpenContent?: ChatHostHandlers['onOpenContent'];
  /**
   * Hosts that wrap ChatView (e.g. the workspace `AgentNode`) can pass
   * the remaining `ChatHostHandlers` here — mention clicks routed to
   * spawn participant widgets, replay clicks on historical `delegate`
   * tool-call rows, and identity hints used by the delegation viz to
   * tag spawned pucks. Standalone chat surfaces (`/chats/<id>`,
   * `/[username]`) leave this undefined and inherit default
   * navigation.
   */
  onOpenProfile?: ChatHostHandlers['onOpenProfile'];
  onReplayDelegation?: ChatHostHandlers['onReplayDelegation'];
  /**
   * Host override for the "Turn on app building" card button (see
   * `ChatHostHandlers.onOpenAgentSettings`). When omitted, ChatView falls
   * back to spawning the agent on the workspace (`/workspace?chat=<name>`),
   * where the tile's Settings toolbar entry is one click away; there is no
   * standalone agent-settings route outside the canvas.
   */
  onOpenAgentSettings?: ChatHostHandlers['onOpenAgentSettings'];
  hostingAgentItemId?: ChatHostHandlers['hostingAgentItemId'];
  rootHostingAgentItemId?: ChatHostHandlers['rootHostingAgentItemId'];
  /**
   * Workspace context — when this chat panel is embedded inside a
   * workspace `AgentNode`, the host passes the active `workspaceId`
   * and a stable per-tab id. Both are forwarded as headers
   * (`x-workspace-id`, `x-workspace-tab-id`) on every chat send so
   * the agent runtime can mount the `workspace.widgets.*` tool
   * manifest and route tool calls back to this specific tab via
   * `workspace.bus`. Omit (or leave undefined) for non-workspace
   * chats — the manifest stays empty and no widget calls are routed.
   */
  workspaceId?: string;
  workspaceTabId?: string;
}

function ScrollToBottomButton() {
  const t = useT();
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();
  if (isAtBottom) return null;
  return (
    <button
      type="button"
      onClick={() => scrollToBottom()}
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10
        bg-background/90 border border-border rounded-full p-2
        shadow-md hover:bg-accent transition-opacity animate-in fade-in duration-200"
      aria-label={t('chatView.scrollToBottom')}
    >
      <ChevronDown className="h-4 w-4" />
    </button>
  );
}

export function ChatView({
  chatId,
  draft,
  onBack,
  onChatCreated,
  embedded,
  prefill,
  hideHeader,
  onOpenContent,
  onOpenProfile,
  onReplayDelegation,
  onOpenAgentSettings,
  hostingAgentItemId,
  rootHostingAgentItemId,
  workspaceId,
  workspaceTabId,
}: ChatViewProps) {
  const isDraft = !chatId && !!draft;
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  // A GUEST who runs into the agent paywall is prompted to CREATE AN ACCOUNT
  // (which grants demo credits — see lib/balance/demo.ts) rather than to top up
  // a wallet they don't have yet. No-ops outside the workspace (the provider
  // returns a resolve(true) stub), so callers elsewhere keep the top-up dialog.
  const { requireSignup } = useSignupGate();
  const { t } = useTranslation();
  const { isConnected, joinChat, leaveChat, sendTyping, sendCancel, on } = useUAMP();
  const { trackChatSend, trackChatStop } = useAnalytics();

  // Build the request headers for any agent-routing-triggering POST.
  // We always add JSON content-type, and when the host is a workspace
  // panel we additionally stamp `x-workspace-id` + `x-workspace-tab-id`
  // so the server can a) mount `workspace.widgets.*` tools on the LLM,
  // and b) target widget commands back to THIS tab via workspace.bus.
  // Plain (non-workspace) chats produce the same minimal Content-Type
  // header that all existing call sites used before this feature.
  const sendHeaders = useMemo<Record<string, string>>(() => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (workspaceId) h['x-workspace-id'] = workspaceId;
    if (workspaceTabId) h['x-workspace-tab-id'] = workspaceTabId;
    return h;
  }, [workspaceId, workspaceTabId]);

  // ---- Core message state (single reducer) ----
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const { messages, overlays } = state;
  const isAgentWorking = messages.some(m => m.metadata?.status === 'streaming');

  // ---- UI-only state (doesn't affect message rendering) ----
  // What the server (or the draft path) told us about this chat. `chat`
  // below falls back to a shell built from `chatId` + the viewer, so the
  // composer never waits on this record (2026-09-06 lock-out, see
  // `shellChat`).
  const [loadedChat, setChat] = useState<Chat | null>(null);
  // `loading` is the full-view spinner; `historyState` is what the message
  // pane says about the initial history fetch once the composer is up.
  const [loading, setLoading] = useState(true);
  const [historyState, setHistoryState] = useState<'pending' | 'ready' | 'failed'>('pending');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  useEffect(() => {
    if (!sendError) return;
    const t = setTimeout(() => setSendError(null), 5000);
    return () => clearTimeout(t);
  }, [sendError]);
  const [input, setInput] = useState('');
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());
  // A turn the payer's daily spending limit is holding (2026-10-08;
  // components/chat/spending-hold.ts says why it is not an overlay). The ref
  // mirrors the state for the socket handlers, which close over one render.
  const [heldTurn, setHeldTurn] = useState<HeldTurn | null>(null);
  const heldTurnRef = useRef<HeldTurn | null>(null);
  const holdTurn = useCallback((held: HeldTurn | null) => { heldTurnRef.current = held; setHeldTurn(held); }, []);
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserSearch, setAddUserSearch] = useState('');
  const [addUserResults, setAddUserResults] = useState<Array<{id: string; username: string; displayName: string; avatarUrl?: string; accountType: 'human' | 'agent'}>>([]);
  const [addingUser, setAddingUser] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [agentUnreachable, setAgentUnreachable] = useState<{ agentUsername: string; reason?: string } | null>(null);
  const [showTopUpDialog, setShowTopUpDialog] = useState(false);
  /**
   * Inline notice surfaced when a payment error came in for a bridged
   * conversation (Telegram / WhatsApp / etc) and we deliberately suppressed
   * the global top-up dialog. The error is on behalf of the contact, not
   * the viewing owner — the routing path already nudged the contact to
   * sign in, so the owner just needs to know we tried.
   */
  const [bridgedPaymentNotice, setBridgedPaymentNotice] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const justPrependedRef = useRef(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [lightboxItems, setLightboxItems] = useState<LightboxItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [agentGreeting, setAgentGreeting] = useState<{ greetingMessage?: string; greetingPrompt?: string; suggestedActions?: string[]; heroImage?: string; displayName?: string } | null>(null);
  const [inputPulsating, setInputPulsating] = useState(false);
  // Owner → bridged-platform outbound. Defaults to `true` so a portal
  // reply in a bridged DM auto-mirrors to the platform. The initial
  // value is overridden on chat load by `chat.metadata.deliveryMode`
  // (set in lib/messaging/service.ts:updateChatMetadata + the PUT
  // /api/messages/[chatId] route). Toggling persists the new value
  // back so the choice survives reloads — one delivery preference per
  // chat, matching the per-conversation reply-routing UX from the
  // messaging-bridge plan.
  const [bridgeOutbound, setBridgeOutbound] = useState(true);
  const [persistingDeliveryMode, setPersistingDeliveryMode] = useState(false);

  // ---- Refs ----
  const stickyInstance = useStickToBottom({ resize: 'smooth', initial: 'instant' });
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const lastMessageId = useRef<string | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);
  const initialLoadDone = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const waitingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset all chat-scoped state when chatId changes.
  // Skip the reset when transitioning from no-chatId (draft) to a real chatId:
  // the draft already populated `chat` (synthetic) and `messages` is empty, so
  // we let the next fetchChat replace them in place. This preserves the
  // textarea (no spinner unmount) and keeps input focus after the user sends
  // their first message in a draft chat.
  const prevChatIdRef = useRef<string | undefined>(chatId);
  useEffect(() => {
    const prev = prevChatIdRef.current;
    prevChatIdRef.current = chatId;
    if (!prev && chatId) {
      // draft → real chat; let fetchChat refresh data without clearing UI.
      return;
    }
    initialLoadDone.current = false;
    lastMessageId.current = null;
    dispatch({ type: 'MESSAGES_LOADED', messages: [], isInitial: true });
    setChat(null);
    setLoading(true);
    setHistoryState('pending');
    setHasMore(true);
    setAgentGreeting(null);
    streamBuffersRef.current.clear();
    dirtyResponseIds.current.clear();
    displayCursorsRef.current.clear();
    pauseFramesRef.current.clear();
    if (streamAnimationRef.current) { cancelAnimationFrame(streamAnimationRef.current); streamAnimationRef.current = null; }
    abortRef.current?.abort();
    abortRef.current = null;
    setBridgeOutbound(true);
  }, [chatId]);

  // Multi-buffer streaming refs (one per concurrent response)
  const streamBuffersRef = useRef<Map<string, StreamingItem[]>>(new Map());
  const dirtyResponseIds = useRef<Set<string>>(new Set());
  const flushRafRef = useRef<number | null>(null);

  // Perception-aware streaming throttle refs
  const displayCursorsRef = useRef<Map<string, number>>(new Map());
  const pauseFramesRef = useRef<Map<string, number>>(new Map());
  const streamAnimationRef = useRef<number | null>(null);

  const resizeTextarea = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    // When the input is empty, drop any inline `height` so the CSS
    // `min-h-[44px]` owns the single-line size. Without this, the
    // delegation-puck → expanded-widget transition can measure
    // `scrollHeight` against a parent that hasn't settled yet (the
    // widget is still animating from puck size to full size), pin an
    // inflated inline height (often clamped to the 180 cap), and the
    // composer never shrinks back. Reproduced on FLUX sub-chat after
    // closing/reopening the agent puck. Once the user types, we
    // switch back to the scrollHeight-driven autosize path.
    if (!el.value) {
      el.style.height = '';
      el.style.overflowY = 'hidden';
      return;
    }
    el.style.height = 'auto';
    const clamped = Math.min(el.scrollHeight, 180);
    el.style.height = `${clamped}px`;
    el.style.overflowY = clamped >= 180 ? 'auto' : 'hidden';
  }, []);

  // Belt-and-suspenders for programmatic `setInput` callers (handleSend
  // post-send clear, suggested-prompt buttons, prefill effects, etc.).
  // `onChange` doesn't fire when state changes outside a typed keystroke,
  // so without this any caller that forgets to schedule its own RAF
  // re-measure would leave the textarea expanded.
  useLayoutEffect(() => {
    resizeTextarea();
  }, [input, resizeTextarea]);

  const textareaRefCallback = useCallback((el: HTMLTextAreaElement | null) => {
    (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
    if (el) resizeTextarea();
  }, [resizeTextarea]);

  // Prefill (works for both normal prefill prop and draft.prefillText)
  const lastAppliedPrefill = useRef<string | undefined>(undefined);
  const effectivePrefill = prefill || (isDraft ? draft.prefillText : undefined);
  useEffect(() => {
    if (effectivePrefill && effectivePrefill !== lastAppliedPrefill.current) {
      lastAppliedPrefill.current = effectivePrefill;
      setInput(effectivePrefill);
      setInputPulsating(true);
      const focusTimer = setTimeout(() => {
        inputRef.current?.focus();
        const len = effectivePrefill.length;
        inputRef.current?.setSelectionRange(len, len);
        resizeTextarea();
      }, 150);
      const pulseTimer = setTimeout(() => setInputPulsating(false), 3000);
      return () => { clearTimeout(focusTimer); clearTimeout(pulseTimer); };
    }
  }, [effectivePrefill, resizeTextarea]);

  // Embedded live-chat auto-focus.
  //
  // The agent widget keys ChatView on `chatId ?? draft:username`, so:
  //   • sending the first message (draft → live) remounts with the
  //     real chatId and otherwise leaves the textarea unfocused, and
  //   • picking a chat from the "Chats" pane also remounts with a new
  //     chatId.
  // In both cases the user is clearly engaged with this chat and
  // expects to keep typing — auto-focus the composer once the
  // textarea has mounted. Scoped to `embedded` so the standalone
  // `/chat/[id]` route doesn't steal focus from the user's scroll
  // position or any link they were about to click. Draft mode is
  // excluded because its own effect already handles focus (above).
  useEffect(() => {
    if (!embedded) return;
    if (isDraft || !chatId) return;
    const t = setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }, 50);
    return () => clearTimeout(t);
  }, [embedded, isDraft, chatId]);

  // Draft mode: build a synthetic chat OPTIMISTICALLY and clear the
  // spinner immediately, then enrich the participant from `/api/users`
  // in the background.
  //
  // Previously this held `loading` true until the `/api/users` fetch
  // resolved — so an embedded agent widget whose profile round-trip was
  // slow or stalled showed a PERMANENT spinner instead of the greeting,
  // and a null `user` bailed before ever clearing `loading`. We already
  // know the participant's username (and, for agent widgets, that it's
  // an agent), so paint the greeting/composer right away.
  useEffect(() => {
    if (!isDraft) return;
    if (!user) {
      // No self-participant yet (auth still settling) — don't trap the
      // widget on a spinner; let it fall through to its empty state.
      setLoading(false);
      return;
    }
    let cancelled = false;
    const optimisticAccountType: 'human' | 'agent' = draft.participantIsAgent
      ? 'agent'
      : 'human';
    setChat({
      id: '',
      type: 'dm',
      createdById: user.id,
      participants: [
        {
          userId: user.id,
          username: user.username,
          displayName: user.displayName || user.username,
          avatarUrl: user.avatarUrl ?? undefined,
          accountType: (user.accountType || 'human') as 'human' | 'agent',
        },
        {
          userId: '',
          username: draft.participantUsername,
          displayName: draft.participantUsername,
          avatarUrl: undefined,
          accountType: optimisticAccountType,
        },
      ],
    });
    setLoading(false);
    setTimeout(() => {
      inputRef.current?.focus();
      const len = inputRef.current?.value.length ?? 0;
      inputRef.current?.setSelectionRange(len, len);
    }, 150);
    // Background enrichment — patch the real id / displayName / avatar /
    // accountType in once the profile resolves. Failure is non-fatal:
    // the optimistic participant stays usable.
    fetch(`/api/users/${encodeURIComponent(draft.participantUsername)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const p = data?.user;
        if (!p) return;
        setChat((prev) =>
          prev
            ? {
                ...prev,
                participants: prev.participants.map((pp) =>
                  pp.username === draft.participantUsername
                    ? {
                        userId: p.id,
                        username: p.username,
                        displayName: p.displayName || p.username,
                        avatarUrl: p.avatarUrl,
                        accountType: p.accountType,
                      }
                    : pp,
                ),
              }
            : prev,
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isDraft, draft?.participantUsername, draft?.participantIsAgent, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The composer needs only `chatId` and the viewer; message history is a
  // separate concern. Until 2026-09-06 the render gate returned null while
  // the record was unloaded, so under load (history route answering in 30
  // to 60s, watchdog aborting every attempt) the agent node painted NO
  // composer and the person could not talk to their agent precisely when
  // the server was already struggling. The shell carries the viewer as the
  // only known participant: `otherParticipant` is undefined, so
  // identity-driven chrome (agent tint, greeting, rename) stays off until
  // the real record lands and replaces it in place. The hosts that matter
  // here (workspace AgentNode, library panel) hide the header anyway.
  const shellChat = useMemo<Chat | null>(() => {
    if (!chatId || !user) return null;
    return {
      id: chatId,
      type: 'dm',
      participants: [
        {
          userId: user.id,
          username: user.username,
          displayName: user.displayName || user.username,
          avatarUrl: user.avatarUrl ?? undefined,
          accountType: (user.accountType || 'human') as 'human' | 'agent',
        },
      ],
    };
  }, [chatId, user?.id, user?.username, user?.displayName, user?.avatarUrl, user?.accountType]); // eslint-disable-line react-hooks/exhaustive-deps
  const chat = loadedChat ?? shellChat;

  // Computed
  const otherParticipant = chat?.participants.find((p) => p.userId !== user?.id);
  const isAgent = otherParticipant?.accountType === 'agent';
  const displayName = chat?.name || otherParticipant?.displayName || otherParticipant?.username || 'Chat';
  const canEditChat = Boolean(chat && user && chat.createdById === user.id);
  // Who may SEAT somebody in a shared conversation: the person who started it
  // (S-036, 2026-09-08). Not who may BE seated, which is anybody: a person is
  // an ordinary participant, screened at the door by every seated agent's own
  // acceptFrom like any other newcomer.
  //
  // The search seam is hidden for a non-creator because `handleAddUser` only
  // acts on `res.ok`, so a refused add would look like a click that did
  // nothing. The panel still opens for everyone, because it is also how a
  // participant reads who is in the room.
  // The mode is read straight off the metadata bag
  // because the canonical reader (`readChatA2aMode`, lib/messaging/service.ts)
  // is a server module; the two agree on the one fact that matters, which
  // is `metadata.a2a.mode` being one of the two modes.
  const isAgentRoom = Boolean(
    (() => {
      const mode = (chat?.metadata as { a2a?: { mode?: unknown } } | undefined)?.a2a?.mode;
      return mode === 'conversation' || mode === 'delegation';
    })(),
  );
  const canAddParticipants = !isAgentRoom || canEditChat;

  // ---- Helpers ----
  const clearTimers = useCallback(() => {
    if (waitingTimerRef.current) { clearTimeout(waitingTimerRef.current); waitingTimerRef.current = null; }
    if (finalizeTimerRef.current) { clearTimeout(finalizeTimerRef.current); finalizeTimerRef.current = null; }
    if (flushRafRef.current) { cancelAnimationFrame(flushRafRef.current); flushRafRef.current = null; }
  }, []);

  const handleStop = useCallback(() => {
    haptic('medium');
    if (chatId && !isDraft) {
      sendCancel(chatId);
      trackChatStop(chatId);
    }
    clearTimers();
    streamBuffersRef.current.clear();
    dirtyResponseIds.current.clear();
    displayCursorsRef.current.clear();
    pauseFramesRef.current.clear();
    if (streamAnimationRef.current) { cancelAnimationFrame(streamAnimationRef.current); streamAnimationRef.current = null; }
    dispatch({ type: 'CANCEL' });
  }, [chatId, isDraft, sendCancel, clearTimers, trackChatStop]);

  const handleBackRef = useRef(() => { if (onBack) onBack(); else router.push('/'); });
  handleBackRef.current = () => { if (onBack) onBack(); else router.push('/'); };
  const handleBack = useCallback(() => handleBackRef.current(), []);

  // ---- Data fetching ----
  // fetchChat must be stable: deps are only chatId + !!user so the
  // initial-fetch effect doesn't re-fire on unrelated re-renders.
  const userRef = useRef(user);
  userRef.current = user;
  // Mirror of the reducer's message count so the stable `fetchChat` can
  // tell whether anything is already on screen when a slow history lands.
  const messagesCountRef = useRef(0);
  messagesCountRef.current = messages.length;
  // Position on the HISTORY_FETCH_TIMEOUTS_MS ladder for the initial chat
  // fetch. Reset whenever the target chat changes (see the initial-fetch
  // effect) and by the manual retry in the message pane.
  const fetchRetryRef = useRef(0);
  const fetchChat = useCallback(async () => {
    if (!userRef.current || isDraft || !chatId) {
      sdbg(`fetchChat: skip user=${!!userRef.current} isDraft=${isDraft} chatId=${chatId ?? 'none'}`);
      return;
    }
    const fetchTag = `fetch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    sdbg(`fetchChat START tag=${fetchTag} chatId=${chatId}`);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    // Per-attempt watchdog: a response that HANGS (socket open, reply never
    // arriving, pod mid-roll) fires no error and, before the first load,
    // nothing else re-fires this fetch, so the view stayed blank forever
    // (same silent-hang class as dev 233-pre, 2026-08-11). The flag lets the
    // catch tell a watchdog abort (transient, bounded retry below) from a
    // supersede/unmount abort (return): every other abort path also nulls or
    // replaces `abortRef`, so `abortRef.current === controller` plus the flag
    // uniquely identifies a timeout.
    //
    // The bound escalates per attempt (HISTORY_FETCH_TIMEOUTS_MS) instead of
    // staying at a flat 15s. On 2026-09-06 two browser runs on the local
    // cluster had this route answering in 30 to 60s under load: a flat 15s
    // aborted every attempt, the 1s/2s retries turned that into a tight
    // abort/retry loop (`Error fetching chat: AbortError` on repeat), and
    // because the composer was gated on this fetch the person was locked out
    // of their agent while the server was already behind. A merely-slow
    // server is not a hung socket, and aborting a slow query only to re-issue
    // it adds load to the thing that is struggling. 15s stays for the first
    // attempt because a true hang is still the more common failure and the
    // composer no longer waits on this fetch (HISTORY_REVEAL_MS, `shellChat`);
    // later attempts get 30s then 60s so a slow-but-alive server gets one
    // honest chance at each latency we actually observed. Worst case is about
    // 108s of attempts, then a stated failure with a manual retry in the
    // message pane, never a blank pane or a spinner. The ladder position is
    // kept for background refetches after a load on purpose: a server that
    // was slow at load is still slow.
    let watchdogFired = false;
    const attempt = Math.min(fetchRetryRef.current, HISTORY_FETCH_TIMEOUTS_MS.length - 1);
    const watchdog = setTimeout(() => {
      watchdogFired = true;
      controller.abort();
    }, HISTORY_FETCH_TIMEOUTS_MS[attempt]);
    // Bounded, backed-off retry for transient failures on the initial load
    // (5xx, connection reset by a rolling pod, LB blip, watchdog timeout).
    // Returns false once the ladder is spent so the caller can mark the
    // history as failed. Background refetches after the first load never
    // retry: the view already has content and the next WS event or poll
    // re-fires the fetch anyway.
    const scheduleRetry = (): boolean => {
      if (initialLoadDone.current) return false;
      const next = fetchRetryRef.current + 1;
      if (next >= HISTORY_FETCH_TIMEOUTS_MS.length) return false;
      fetchRetryRef.current = next;
      const delay = HISTORY_FETCH_RETRY_DELAYS_MS[Math.min(next - 1, HISTORY_FETCH_RETRY_DELAYS_MS.length - 1)];
      setTimeout(() => {
        if (abortRef.current === controller && (watchdogFired || !controller.signal.aborted)) void fetchChat();
      }, delay);
      return true;
    };
    try {
      const res = await fetch(`/api/messages/${chatId}?limit=50`, { signal: controller.signal });
      if (controller.signal.aborted) { sdbg(`fetchChat ABORTED post-fetch tag=${fetchTag}`); return; }
      if (res.ok) {
        const data = await res.json();
        if (controller.signal.aborted) { sdbg(`fetchChat ABORTED post-json tag=${fetchTag}`); return; }
        setChat(prev => {
          if (!prev) return data.chat;
          if (JSON.stringify(prev) === JSON.stringify(data.chat)) return prev;
          return data.chat;
        });
        // Hydrate the per-chat reply-routing toggle from
        // chat.metadata.deliveryMode set by the messaging-bridge plan
        // (lib/messaging/service.ts:updateChatMetadata). Only override
        // the default when an explicit value is present so unrelated
        // chats keep the optimistic "auto-bridge" default.
        const persistedMode = data.chat?.metadata?.deliveryMode;
        if (persistedMode === 'bridge') setBridgeOutbound(true);
        else if (persistedMode === 'portal_only') setBridgeOutbound(false);
        const newMessages: Message[] = data.messages.reverse();
        setHasMore(data.messages.length >= 50);

        const firstLoad = !initialLoadDone.current;
        if (firstLoad) initialLoadDone.current = true;
        // Wholesale replace only when nothing is on screen yet. With the
        // composer decoupled from this fetch (2026-09-06) the person may
        // already have sent a message (optimistic `temp-` row, POST still in
        // flight) or be watching a reply stream in over the socket by the
        // time a slow history lands; the non-initial merge keeps those,
        // while a replace would drop the optimistic row and its later
        // OPTIMISTIC_CONFIRM would find nothing to confirm.
        const isInitial = firstLoad && messagesCountRef.current === 0;
        if (STREAM_DBG) {
          const lastAsst = [...newMessages].reverse().find(m => m.role === 'assistant');
          sdbg(`fetchChat OK tag=${fetchTag} isInitial=${isInitial} count=${newMessages.length} lastAsst.id=${lastAsst?.id ?? 'none'} lastAsst.contentLen=${(lastAsst?.content || '').length} lastAsst.status=${lastAsst?.metadata?.status ?? 'none'} lastAsst.contentItems=${lastAsst?.contentItems?.length ?? 0}`);
        }
        dispatch({ type: 'MESSAGES_LOADED', messages: newMessages, isInitial });
        setHistoryState('ready');

        if (newMessages.length > 0) {
          const latestId = newMessages[newMessages.length - 1].id;
          if (latestId !== lastMessageId.current) {
            lastMessageId.current = latestId;
            fetch(`/api/messages/${chatId}`, { method: 'PATCH' })
              .then(() => window.dispatchEvent(new CustomEvent('messagesRead')))
              .catch(() => {});
          }
        }
      } else {
        sdbg(`fetchChat ERROR tag=${fetchTag} status=${res.status}`);
        // Don't navigate away on fetch failure: keep whatever shell is
        // rendered so embedded contexts (AgentNode, workspace pucks) don't
        // bounce the user to the chats list on transient errors.
        //
        // A 5xx before the first successful load previously left the record
        // null FOREVER (nothing re-fired the fetch), so one blip from a pod
        // mid-roll made the chat permanently blank. Retry transient statuses
        // up the ladder; 4xx is a definitive answer and stays terminal. Either
        // way, once nothing more will be tried the pane says so and offers a
        // retry instead of sitting empty.
        const retried = res.status >= 500 && scheduleRetry();
        if (!retried && !initialLoadDone.current) setHistoryState('failed');
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError' && !watchdogFired) {
        sdbg(`fetchChat AbortError tag=${fetchTag}`);
        return;
      }
      console.error('Error fetching chat:', error);
      sdbg(`fetchChat THROWN tag=${fetchTag} err=${(error as Error).message}${watchdogFired ? ' (watchdog timeout)' : ''}`);
      // Same bounded retry for network-level failures (connection reset by
      // a rolling pod, LB blip) and watchdog timeouts on the initial load.
      if (!scheduleRetry() && !initialLoadDone.current) setHistoryState('failed');
    } finally {
      clearTimeout(watchdog);
      // Supersede/unmount aborts leave `loading` to the successor; a
      // watchdog abort has no successor unless the retry above fires, so it
      // must clear the spinner itself. Retries run with the composer already
      // up: nothing sets `loading` back to true until the chat changes.
      if (!controller.signal.aborted || watchdogFired) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, handleBack]);

  // Manual retry from the message pane once the ladder is spent. Starts the
  // ladder over: the person asked, so they get the full budget again.
  const retryHistory = useCallback(() => {
    fetchRetryRef.current = 0;
    setHistoryState('pending');
    void fetchChat();
  }, [fetchChat]);

  useEffect(() => {
    if (!chat || chat.type !== 'dm' || !isAgent || !otherParticipant || messages.length > 0 || loading) return;
    fetch(`/api/agents/${encodeURIComponent(otherParticipant.username)}?greeting=1`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const c = data?.config;
        if (!data?.agent) return;
        // EVERY agent gets a welcome screen, configured or not. Until
        // 2026-09-10 this only ran when a greeting field was set, and an
        // agent born through `create_agent` sets none of them unless the
        // model thought to pass one: its chat window opened completely
        // blank, which reads as a broken agent rather than a new one. The
        // fallback is the agent's own identity — avatar, name, and the
        // description it was created with — so the pane says who the person
        // is talking to. A configured greeting still wins, untouched.
        setAgentGreeting({
          greetingMessage: c?.greetingMessage || c?.description || data.agent.bio || undefined,
          greetingPrompt: c?.greetingPrompt || undefined,
          suggestedActions: c?.suggestedActions,
          heroImage: c?.greetingImageDesktop || c?.greetingImageMobile || undefined,
          displayName: c?.greetingMessage ? undefined : (data.agent.displayName || data.agent.username || undefined),
        });
      })
      .catch(() => {});
  }, [chat, isAgent, otherParticipant, messages.length, loading]);

  const loadMoreMessages = useCallback(async () => {
    if (!user || loadingMore || !hasMore || messages.length === 0) return;
    const oldestMessage = messages[0];
    if (!oldestMessage) return;

    setLoadingMore(true);
    try {
      const res = await fetch(`/api/messages/${chatId}?limit=50&before=${encodeURIComponent(oldestMessage.createdAt)}`);
      if (res.ok) {
        const data = await res.json();
        const olderMessages = data.messages.reverse();
        if (olderMessages.length > 0) {
          const container = stickyInstance.scrollRef.current;
          const prevScrollHeight = container?.scrollHeight || 0;
          justPrependedRef.current = true;
          dispatch({ type: 'MESSAGES_PREPENDED', messages: olderMessages });
          setHasMore(data.messages.length >= 50);
          requestAnimationFrame(() => {
            if (container) {
              container.scrollTop = container.scrollHeight - prevScrollHeight;
            }
          });
        } else {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [chatId, user, loadingMore, hasMore, messages]);

  // Initial fetch — runs once per chatId (and when user becomes available), skip in draft
  useEffect(() => {
    if (isDraft || !chatId) return;
    fetchRetryRef.current = 0;
    setHistoryState('pending');
    fetchChat();
    if (!user) return;
    // Reveal the composer on a timer that is independent of the fetch: a
    // slow history is not a reason to withhold the person's only way to act
    // (2026-09-06 lock-out). The healthy path (sub-second) never sees this;
    // past HISTORY_REVEAL_MS the shell header + composer paint and the pane
    // shows its own spinner until history lands or the ladder is spent.
    const reveal = setTimeout(() => setLoading(false), HISTORY_REVEAL_MS);
    return () => clearTimeout(reveal);
  }, [chatId, isDraft, !!user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling: only when WS disconnected, skip in draft. Gated on the LOADED
  // record, not the shell: before the first successful load the ladder in
  // fetchChat owns fetching, and a 15s poll superseding an in-flight 30s
  // attempt would recreate the 2026-09-06 abort loop.
  useEffect(() => {
    if (isDraft || !user || !loadedChat) return;
    if (isConnected) return;
    const interval = setInterval(fetchChat, 15_000);
    return () => clearInterval(interval);
  }, [isDraft, user, loadedChat, fetchChat, isConnected]);

  // ---- Scrolling (handled by use-stick-to-bottom) ----
  // Scroll-to-top pagination
  useEffect(() => {
    const container = stickyInstance.scrollRef.current;
    if (!container || !hasMore) return;
    const handleScroll = () => {
      if (container.scrollTop < 100 && !loadingMore && hasMore) loadMoreMessages();
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadingMore, loadMoreMessages, stickyInstance.scrollRef]);

  // ---- WS lifecycle ----
  const fetchChatRef = useRef(fetchChat);
  fetchChatRef.current = fetchChat;
  useEffect(() => {
    if (isDraft || !chatId) return;
    if (isConnected && chatId) {
      joinChat(chatId);
      if (initialLoadDone.current) fetchChatRef.current();
      return () => { leaveChat(chatId); };
    }
  }, [isDraft, isConnected, chatId, joinChat, leaveChat]);

  // ---- WS event handlers (each handler = single dispatch) ----
  useEffect(() => {
    if (!isConnected) return;

    const unsubTyping = on('chat:typing', (data) => {
      if (data.chatId !== chatId || data.userId === user?.id) return;
      setTypingUsers((prev) => {
        const next = new Map(prev);
        const uid = data.userId;
        if (uid == null) return prev;
        if (data.isTyping) next.set(uid, data.username ?? '');
        else next.delete(uid);
        return next;
      });
    });

    const unsubMessage = on('chat:message', (payload: unknown) => {
      const msg = payload as Message;
      if (msg.chatId !== chatId) return;
      if (msg.senderId) {
        setTypingUsers((prev) => { const next = new Map(prev); next.delete(msg.senderId); return next; });
      }
      dispatch({ type: 'WS_MESSAGE', message: msg });
      fetch(`/api/messages/${chatId}`, { method: 'PATCH' })
        .then(() => window.dispatchEvent(new CustomEvent('messagesRead')))
        .catch(() => {});
    });

    const unsubMessageUpdated = on('chat:message_updated', (payload) => {
      const updated = payload as unknown as Message;
      const passes = updated.chatId === chatId;
      sdbg(`handler chat:message_updated: messageId=${updated.id} payload.chatId=${updated.chatId ?? 'none'} viewChatId=${chatId ?? 'none'} filterPassed=${passes} status=${updated.metadata?.status ?? 'none'} contentLen=${(updated.content || '').length}`);
      if (!passes) return;
      dispatch({ type: 'MESSAGE_UPDATED', message: updated });
    });

    const unsubMessageDeleted = on('chat:message_deleted', ({ messageId: deletedId, chatId: deletedChatId }) => {
      if (deletedChatId !== chatId) return;
      dispatch({ type: 'MESSAGE_DELETED', messageId: deletedId });
    });

    const unsubOnline = on('presence:online', (data) => {
      setChat((prev) => {
        if (!prev) return prev;
        return { ...prev, participants: prev.participants.map((p) => p.userId === data.userId ? { ...p, isOnline: true } : p) };
      });
    });

    const unsubOffline = on('presence:offline', (data) => {
      setChat((prev) => {
        if (!prev) return prev;
        return { ...prev, participants: prev.participants.map((p) => p.userId === data.userId ? { ...p, isOnline: false } : p) };
      });
    });

    const unsubAgentUnreachable = on('chat:agent_unreachable', (data) => {
      if (data.chatId !== chatId) return;
      setAgentUnreachable({ agentUsername: data.agentUsername, reason: data.reason ?? undefined });
      setTimeout(() => setAgentUnreachable((prev) => (prev?.agentUsername === data.agentUsername ? null : prev)), 8000);
    });

    // --- Streaming: multi-buffer, flush at RAF rate ---
    const applyDelta = (prev: StreamingItem[], delta: { type?: string; text?: string; tool_call?: any; tool_result?: any; tool_progress?: any; thinking?: { content?: string }; content_id?: string; filename?: string; mime_type?: string; metadata?: Record<string, unknown> }): StreamingItem[] => {
      if (delta.type === 'file' && delta.content_id) {
        console.log('[chat] file delta received:', delta.content_id, delta.filename);
        // Workspace canvas: notify any AgentNode hosting this chat so it can
        // drop the new content widget next to the originating agent.
        if (chatId) {
          emitWorkspaceSpawn({
            chatId,
            type: 'content',
            refId: delta.content_id,
            cache: {
              displayName: delta.filename,
              mimeType: delta.mime_type,
            },
          });
        }
        return [...prev, { type: 'file' as const, content_id: delta.content_id, filename: delta.filename || 'file', mime_type: delta.mime_type || 'application/octet-stream', metadata: delta.metadata }];
      }
      if (delta.type === 'tool_call' && delta.tool_call) {
        const tcId = delta.tool_call.id || `tc-${Date.now()}`;
        const existingIdx = prev.findIndex(i => i.type === 'tool_call' && i.id === tcId);
        if (existingIdx >= 0) {
          const ex = prev[existingIdx] as Extract<StreamingItem, { type: 'tool_call' }>;
          const merged = {
            ...ex,
            name: delta.tool_call.name || ex.name,
            arguments: delta.tool_call.arguments || ex.arguments,
          };
          console.log(`[chat] applyDelta tool_call merge: name=${merged.name} id=${tcId}`);
          const updated = [...prev];
          updated[existingIdx] = merged;
          return updated;
        }
        console.log(`[chat] applyDelta tool_call new: name=${delta.tool_call.name} id=${tcId}`);
        return [...prev, { type: 'tool_call' as const, id: tcId, name: delta.tool_call.name || 'unknown', arguments: delta.tool_call.arguments || '{}' }];
      }
      if (delta.type === 'tool_result' && delta.tool_result) {
        // text_editor in-place edits (`str_replace` / `insert` /
        // `undo_edit`) mutate the existing content row + append a new
        // version (see lib/llm/platform-tool-handlers.ts). When the
        // workspace canvas has a ContentNode pointing at the same
        // contentId, it needs to know to refetch the body so the
        // iframe re-renders with the new code — without this, the
        // user has to reload the page to see agent edits, even though
        // the row is fully written. Fire a window CustomEvent the
        // ContentNode subscribes to (filtered by refId). The event is
        // harmless noise outside a workspace.
        //
        // NOTE on key shape: the streaming delta is built in
        // lib/llm/uamp-proxy.ts (~L1944) which puts `command` +
        // `content_id` (snake_case) DIRECTLY on tool_result, NOT
        // nested in `.data`. Initial implementation looked for
        // `.data.contentId` (wrong key + wrong nesting) and the event
        // never fired. Some adapters ALSO surface a `.data` payload
        // with `contentId` (camelCase) on certain tool envelopes —
        // accept both shapes here so we cover every transport that
        // feeds applyDelta.
        const trRaw = delta.tool_result as Record<string, unknown>;
        const trData = trRaw.data as Record<string, unknown> | undefined;
        const trCmd =
          (typeof trRaw.command === 'string' && trRaw.command) ||
          (typeof trData?.command === 'string' && trData.command) ||
          null;
        const trContentId =
          (typeof trRaw.content_id === 'string' && trRaw.content_id) ||
          (typeof trRaw.contentId === 'string' && trRaw.contentId) ||
          (typeof trData?.content_id === 'string' && trData.content_id) ||
          (typeof trData?.contentId === 'string' && trData.contentId) ||
          null;
        // Plan v3-05 folder-bundle reload: when the agent edits a file
        // INSIDE a folder (e.g. `widget.json`, a `tools/*.js` source),
        // workspace ContentNodes that render the FOLDER (not the
        // individual file) should also reload. The server enriches
        // the delta with `parent_id` (uamp-proxy.ts L1944-ish); we
        // include it on the window event so folder-keyed listeners
        // fire too. ContentNodes accept events that match EITHER
        // `event.contentId === refId` OR `event.parentFolderId === refId`.
        const trParentId =
          (typeof trRaw.parent_id === 'string' && trRaw.parent_id) ||
          (typeof trRaw.parentId === 'string' && trRaw.parentId) ||
          (typeof trData?.parent_id === 'string' && trData.parent_id) ||
          (typeof trData?.parentId === 'string' && trData.parentId) ||
          null;
        if (
          trContentId &&
          (trCmd === 'str_replace' || trCmd === 'insert' || trCmd === 'undo_edit' || trCmd === 'create')
        ) {
          try {
            if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('workspace:content-updated', {
                  detail: {
                    contentId: trContentId,
                    parentFolderId: trParentId,
                    command: trCmd,
                  },
                }),
              );
            }
          } catch {
            // Best-effort: a missing CustomEvent constructor on some
            // SSR / test paths shouldn't break tool_result handling.
          }
        }
        return prev.map(item =>
          item.type === 'tool_call' && item.id === delta.tool_result.call_id
            ? {
                ...item,
                result: delta.tool_result.result,
                ...(delta.tool_result.data !== undefined ? { data: delta.tool_result.data as Record<string, unknown> } : {}),
              }
            : item
        );
      }
      if (delta.type === 'tool_progress' && delta.tool_progress) {
        const text = delta.tool_progress.text || '';
        const isReplace = !!delta.tool_progress.replace;
        const isThinking = delta.tool_progress.kind === 'thinking';
        const isSystem = delta.tool_progress.kind === 'system';
        const callId = delta.tool_progress.call_id;
        const hasMatchingCall = !!callId && prev.some(it => it.type === 'tool_call' && it.id === callId);

        // Delegation lifecycle events ride the same envelope but carry a
        // structured `data` payload that drives the workspace canvas's
        // delegation visualisation (collapsed sibling pucks + animated
        // edges). When this chat is embedded inside an `AgentNode` the
        // host passes its own `itemId` through `ChatHostContext` —
        // events are scoped to that hosting agent so a chat that
        // multiple AgentNodes peer-render does not fan out. Outside a
        // workspace host (`/chats/<id>`, standalone) the event is
        // silently dropped — there's no canvas to visualise.
        // `kind: 'present'` events fan out to the workspace canvas via
        // `emitWorkspaceSpawn`. Each variant carries either a real
        // `content_id` (artefacts persisted by the server-side skill —
        // image / code / note / chart) or an inline `payload` (url
        // embeds, widget seeds — no content row). Agent variants spawn
        // a participant widget keyed by user id. Outside a workspace
        // (`/chats/<id>`, standalone) the spawn dispatcher has no
        // subscribers and the event is harmless noise.
        if (delta.tool_progress.kind === 'present' && delta.tool_progress.data && chatId) {
          const d = delta.tool_progress.data as
            | {
                type: 'content';
                content_id: string | null;
                mimeType: string;
                displayName: string;
                payload?: Record<string, unknown>;
              }
            | {
                type: 'agent';
                refId: string;
                cache: Record<string, unknown>;
              };
          if (d.type === 'content') {
            // Inline-payload variants (url-embed, widget) don't have a
            // backing content row — synthesize an `inline:<uuid>` refId
            // so ContentNode's inline-payload branch picks it up and
            // renders from cache rather than fetching.
            const refId =
              d.content_id ??
              (typeof crypto !== 'undefined' && 'randomUUID' in crypto
                ? `inline:${crypto.randomUUID()}`
                : `inline:${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
            emitWorkspaceSpawn({
              chatId,
              type: 'content',
              refId,
              cache: {
                displayName: d.displayName,
                mimeType: d.mimeType,
                ...(d.payload ? { payload: d.payload } : {}),
              },
            });
          } else if (d.type === 'agent') {
            emitWorkspaceSpawn({
              chatId,
              type: 'agent',
              refId: d.refId,
              cache: d.cache,
            });
          }
          // Present events carry no LLM-visible text — drop here so
          // they don't render as an empty progress segment on the
          // present.* tool-call row. The chip itself is also filtered
          // out by the `present` prefix at render time.
          return prev;
        }
        if (delta.tool_progress.kind === 'delegation' && hostingAgentItemId) {
          const d = (delta.tool_progress.data ?? {}) as {
            phase?: 'start' | 'complete' | 'error';
            callId?: string;
            delegated?: { username?: string; id?: string; displayName?: string | null; avatarUrl?: string | null };
            subChatId?: string;
            errorMessage?: string;
          };
          if (d.phase && d.callId && d.delegated?.username) {
            emitDelegationEvent({
              phase: d.phase,
              callId: d.callId,
              hostingAgentItemId,
              rootHostingAgentItemId: rootHostingAgentItemId ?? hostingAgentItemId,
              delegated: {
                username: d.delegated.username,
                id: d.delegated.id,
                displayName: d.delegated.displayName,
                avatarUrl: d.delegated.avatarUrl,
              },
              subChatId: d.subChatId,
              errorMessage: d.errorMessage,
            });
          }
          // Delegation events carry no LLM-visible text — drop here to
          // avoid pushing an empty progress segment onto the tool-call
          // row.
          return prev;
        }

        // System-level / orphaned tool_progress (no matching call_id):
        // surface as a lightweight system_notice in the stream so users see
        // backend events like "Daily spending limit reached..." instead of
        // silently dropping them.
        if (!hasMatchingCall) {
          if (!text) return prev;
          if (isSystem || !callId) {
            const last = prev[prev.length - 1];
            if (last?.type === 'system_notice') {
              const updated = [...prev];
              updated[updated.length - 1] = { type: 'system_notice', text: isReplace ? text : last.text + (last.text.endsWith(text) ? '' : `\n${text}`) };
              return updated;
            }
            console.log(`[chat] applyDelta tool_progress → system_notice (no matching call_id=${callId ?? 'n/a'}): text=${JSON.stringify(text).slice(0, 120)}`);
            return [...prev, { type: 'system_notice' as const, text }];
          }
          return prev;
        }

        // Held for the person's spending approval: the payment skill marks
        // its "waiting" line on the call's card, and the call's next progress
        // (or its result) ends the wait (lib/agents/portal-payment-skill.ts).
        const awaitingApproval = delta.tool_progress.notice === APPROVAL_WAIT_NOTICE;
        return prev.map(item => {
          if (item.type !== 'tool_call' || item.id !== callId) return item;
          const segs = [...(item.progressSegments ?? [])];
          const last = segs[segs.length - 1];
          if (isThinking) {
            if (last?.kind === 'thinking') last.text += text;
            else segs.push({ kind: 'thinking', text });
          } else if (isReplace) {
            if (last?.kind === 'replace') last.text = text;
            else segs.push({ kind: 'replace', text });
          } else {
            if (last?.kind === 'append') last.text += text;
            else segs.push({ kind: 'append', text });
          }
          return { ...item, progressSegments: segs, awaitingApproval };
        });
      }
      if (delta.type === 'thinking' && delta.thinking) {
        const last = prev[prev.length - 1];
        if (last?.type === 'thinking') {
          const updated = [...prev];
          updated[updated.length - 1] = { type: 'thinking', text: last.text + (delta.thinking.content || '') };
          return updated;
        }
        return [...prev, { type: 'thinking' as const, text: delta.thinking.content || '' }];
      }
      if (!delta.text) return prev;
      console.log(`[chat] applyDelta text fallback: delta.type=${delta.type} text=${JSON.stringify(delta.text)?.slice(0, 80)}`);
      const ARTIFACT_RE = /^tool_code\s/i;
      const HALLUCINATED_TOOL_RE = /\[Called tool \w+\([\s\S]*?\)\]/;
      const last = prev[prev.length - 1];
      if (last?.type === 'text') {
        const merged = last.text + delta.text;
        if (ARTIFACT_RE.test(merged.trimStart())) return prev;
        const cleaned = merged.replace(HALLUCINATED_TOOL_RE, '').trim();
        if (cleaned !== merged) {
          if (!cleaned) return prev;
          const updated = [...prev];
          updated[updated.length - 1] = { type: 'text', text: cleaned };
          return updated;
        }
        const updated = [...prev];
        updated[updated.length - 1] = { type: 'text', text: merged };
        return updated;
      }
      if (ARTIFACT_RE.test(delta.text.trimStart())) return prev;
      return [...prev, { type: 'text' as const, text: delta.text }];
    };

    // --- Perception-aware streaming throttle ---
    const STREAM_BASE_SPEED = 2;
    const STREAM_MAX_SPEED = 12;
    const STREAM_RAMP_DIVISOR = 80;
    const STREAM_MAX_RAMP = 4;
    const STREAM_PARAGRAPH_PAUSE = 8;
    const STREAM_LIST_ITEM_PAUSE = 4;
    const STREAM_PAUSE_THRESHOLD = 300;

    const getTotalTextLength = (items: StreamingItem[]): number => {
      let len = 0;
      for (const item of items) {
        if (item.type === 'text') len += item.text.length;
      }
      return len;
    };

    const buildThrottledView = (items: StreamingItem[], cursorPos: number): StreamingItem[] => {
      let remaining = cursorPos;
      const result: StreamingItem[] = [];
      for (const item of items) {
        if (item.type !== 'text') {
          result.push(item);
          continue;
        }
        if (remaining <= 0) break;
        if (remaining >= item.text.length) {
          result.push(item);
          remaining -= item.text.length;
        } else {
          result.push({ type: 'text', text: item.text.slice(0, remaining) });
          remaining = 0;
        }
      }
      return result;
    };

    const detectStructuralPause = (text: string, prevPos: number, newPos: number, pending: number): number => {
      if (pending > STREAM_PAUSE_THRESHOLD) return 0;
      const revealed = text.slice(prevPos, newPos);
      if (revealed.includes('\n\n') || /\n#/.test(revealed)) return STREAM_PARAGRAPH_PAUSE;
      if (/\n[-*] /.test(revealed)) return STREAM_LIST_ITEM_PAUSE;
      return 0;
    };

    const getFullText = (items: StreamingItem[]): string => {
      let s = '';
      for (const item of items) {
        if (item.type === 'text') s += item.text;
      }
      return s;
    };

    const advanceStreamingDisplay = () => {
      streamAnimationRef.current = null;
      let anyActive = false;

      for (const [responseId, buffer] of streamBuffersRef.current) {
        const portal = (streamBuffersRef.current as any).__portal?.get(responseId) as { messageId: string; agentId: string } | undefined;
        if (!buffer || buffer.length === 0 || !portal) continue;

        const totalLen = getTotalTextLength(buffer);
        let cursor = displayCursorsRef.current.get(responseId) ?? 0;

        if (cursor >= totalLen) {
          if (dirtyResponseIds.current.has(responseId)) {
            dispatch({ type: 'OVERLAY_DELTA', responseId, items: buffer, messageId: portal.messageId, agentId: portal.agentId });
            dirtyResponseIds.current.delete(responseId);
          }
          continue;
        }

        anyActive = true;

        const pauseRemaining = pauseFramesRef.current.get(responseId) ?? 0;
        if (pauseRemaining > 0) {
          pauseFramesRef.current.set(responseId, pauseRemaining - 1);
          continue;
        }

        const pending = totalLen - cursor;
        const rampFactor = Math.min(pending / STREAM_RAMP_DIVISOR, STREAM_MAX_RAMP);
        const charsThisFrame = Math.min(Math.round(STREAM_BASE_SPEED * Math.max(rampFactor, 1)), STREAM_MAX_SPEED);
        const prevCursor = cursor;
        cursor = Math.min(cursor + charsThisFrame, totalLen);
        displayCursorsRef.current.set(responseId, cursor);

        const pauseFrames = detectStructuralPause(getFullText(buffer), prevCursor, cursor, totalLen - cursor);
        if (pauseFrames > 0) {
          pauseFramesRef.current.set(responseId, pauseFrames);
        }

        const throttledItems = buildThrottledView(buffer, cursor);
        dispatch({ type: 'OVERLAY_DELTA', responseId, items: throttledItems, messageId: portal.messageId, agentId: portal.agentId });
        dirtyResponseIds.current.delete(responseId);
      }

      if (anyActive) {
        streamAnimationRef.current = requestAnimationFrame(advanceStreamingDisplay);
      }
    };

    const startStreamAnimation = () => {
      if (!streamAnimationRef.current) {
        streamAnimationRef.current = requestAnimationFrame(advanceStreamingDisplay);
      }
    };

    const flushStreamingBuffers = () => {
      flushRafRef.current = null;
      startStreamAnimation();
    };

    const seenDeltaResponseIds = new Set<string>();
    const unsubDelta = on('response.delta', (data) => {
      const responseId = data.response_id as string | undefined;
      const delta = data.delta as { type?: string; text?: string; tool_call?: any; tool_result?: any; tool_progress?: any; thinking?: { content?: string }; content_id?: string; filename?: string; mime_type?: string; metadata?: Record<string, unknown> } | undefined;
      const portal = data._portal;
      if (!responseId || !delta) return;

      // Filter by chatId (defense-in-depth)
      if (portal?.chatId && portal.chatId !== chatId) return;

      // THE SPENDING-LIMIT HOLD (2026-10-08): kept out of the stream buffers,
      // drawn as one line until this response moves again or ends.
      const held = heldTurnFromDelta(data as Parameters<typeof heldTurnFromDelta>[0]);
      if (held) { holdTurn(held); return; }
      if (deltaResumesHeldTurn(heldTurnRef.current, data as Parameters<typeof deltaResumesHeldTurn>[1])) holdTurn(null);

      if (responseId && !seenDeltaResponseIds.has(responseId)) {
        seenDeltaResponseIds.add(responseId);
        sdbg(`handler response.delta FIRST: responseId=${responseId} portal.chatId=${portal?.chatId ?? 'none'} portal.messageId=${portal?.messageId ?? 'none'} viewChatId=${chatId ?? 'none'} delta.type=${delta.type ?? 'none'}`);
      }

      // Get or create buffer for this responseId
      let buffer = streamBuffersRef.current.get(responseId);
      if (!buffer) {
        buffer = [];
        streamBuffersRef.current.set(responseId, buffer);
        requestAnimationFrame(() => stickyInstance.scrollToBottom());
      }

      // Store portal metadata alongside the buffer
      if (portal?.messageId) {
        if (!(streamBuffersRef.current as any).__portal) {
          (streamBuffersRef.current as any).__portal = new Map();
        }
        (streamBuffersRef.current as any).__portal.set(responseId, {
          messageId: portal.messageId,
          agentId: portal.agentId || '',
        });
      }

      const updated = applyDelta(buffer, delta);
      streamBuffersRef.current.set(responseId, updated);
      dirtyResponseIds.current.add(responseId);

      // Non-text deltas (tool_call, file, thinking, tool_result, tool_progress) flush immediately
      if (delta.type && delta.type !== 'text') {
        const cursor = displayCursorsRef.current.get(responseId) ?? 0;
        const throttledItems = buildThrottledView(updated, cursor);
        dispatch({ type: 'OVERLAY_DELTA', responseId, items: throttledItems, messageId: (streamBuffersRef.current as any).__portal?.get(responseId)?.messageId, agentId: (streamBuffersRef.current as any).__portal?.get(responseId)?.agentId || '' });
        dirtyResponseIds.current.delete(responseId);
      }

      if (!flushRafRef.current) {
        flushRafRef.current = requestAnimationFrame(flushStreamingBuffers);
      }
    });

    const unsubError = on('response.error', (data) => {
      const portal = data._portal;
      if (portal?.chatId && portal.chatId !== chatId) return;
      clearTimers();
      // A held turn that failed (the limit denied, or the wait timed out):
      // the line goes, and the error's own notice says why.
      holdTurn(null);

      const errorCode = data.error?.code as string | undefined;
      const errorMessage = data.error?.message || '';
      const isPaymentError =
        errorCode === 'INSUFFICIENT_TOKEN_BALANCE' ||
        errorCode === 'INSUFFICIENT_BALANCE' ||
        errorCode === 'PAYMENT_REQUIRED' ||
        errorCode === 'payment_required' ||
        /insufficient.*(balance|funds)/i.test(errorMessage) ||
        /payment.*required/i.test(errorMessage);

      if (isPaymentError) {
        // Bridged-chat suppression: when this chat is sourced from a
        // messaging platform (Telegram / WhatsApp / etc), the failing
        // request was the platform contact's, not the viewing owner's.
        // Server-side runAgentRouting already filters most of these out
        // (it sends a link nudge for unlinked contact_pays + never
        // broadcasts payment errors in that branch), but a few races
        // can still leak through (e.g. owner_pays + owner-out-of-credits
        // on a thread the owner wasn't actively viewing). Belt-and-
        // suspenders here: surface an inline notice instead of the
        // global top-up dialog when the failure isn't the viewer's own.
        const sourceRaw = (chat?.metadata as { source?: string } | undefined)?.source;
        const isBridgedChat = typeof sourceRaw === 'string' && sourceRaw.length > 0;
        // The error event itself doesn't carry the originating sender
        // (response.error is keyed on the response, not the trigger
        // message). We approximate "sender !== viewer" via the most
        // recent inbound message in the chat: in bridged chats the last
        // inbound is always the synthetic external_contact, never the
        // viewer.
        const lastInbound = [...state.messages]
          .reverse()
          .find((m) => m.role === 'user');
        const senderIsViewer = lastInbound?.senderId === user?.id;
        if (isBridgedChat && !senderIsViewer) {
          setBridgedPaymentNotice(
            errorMessage || t('messages.agentEncounteredError'),
          );
          setTimeout(() => setBridgedPaymentNotice(null), 8000);
          return;
        }
        // A GUEST has no wallet — send them to the signup gate (the new account
        // is granted demo credits) instead of the top-up dialog. Real accounts
        // out of funds still get top-up. `accountType` is 'guest' at runtime
        // even though the client type narrows it to human|agent.
        if ((user?.accountType as string | undefined) === 'guest') {
          void requireSignup({ reason: 'Create a free account to chat with agents.', explicit: true });
          return;
        }
        setShowTopUpDialog(true);
        return;
      }

      if (errorCode === 'max_iterations') {
        streamBuffersRef.current.clear();
        dirtyResponseIds.current.clear();
        displayCursorsRef.current.clear();
        pauseFramesRef.current.clear();
        if (streamAnimationRef.current) { cancelAnimationFrame(streamAnimationRef.current); streamAnimationRef.current = null; }
        dispatch({ type: 'CANCEL' });
        const details = data.error?.details as { agent_username?: string } | undefined;
        setAgentUnreachable({
          agentUsername: details?.agent_username ?? t('messages.unknownAgent'),
          reason: errorMessage || t('messages.maxIterationsReached'),
        });
        setTimeout(() => setAgentUnreachable(null), 15000);
        return;
      }

      const details = data.error?.details as { agent_username?: string } | undefined;
      const reason = errorMessage || t('messages.agentEncounteredError');
      setAgentUnreachable({ agentUsername: details?.agent_username ?? t('messages.unknownAgent'), reason });
      setTimeout(() => setAgentUnreachable(null), 10000);
    });

    const unsubCancelled = on('response.cancelled', () => {
      clearTimers();
      holdTurn(null);
      streamBuffersRef.current.clear();
      dirtyResponseIds.current.clear();
      displayCursorsRef.current.clear();
      pauseFramesRef.current.clear();
      if (streamAnimationRef.current) { cancelAnimationFrame(streamAnimationRef.current); streamAnimationRef.current = null; }
      dispatch({ type: 'CANCEL' });
    });

    const unsubDone = on('response.done', (data) => {
      const portal = data._portal;
      const passes = !(portal?.chatId && portal.chatId !== chatId);
      sdbg(`handler response.done: responseId=${data.response_id ?? 'none'} portal.chatId=${portal?.chatId ?? 'none'} viewChatId=${chatId ?? 'none'} filterPassed=${passes}`);
      if (!passes) return;
      const responseId = data.response_id;
      if (!responseId) return;
      if (heldTurnRef.current?.responseId === responseId) holdTurn(null);

      // Flush any remaining buffer for this responseId (instantly reveal all text)
      if (flushRafRef.current) { cancelAnimationFrame(flushRafRef.current); flushRafRef.current = null; }
      const buffer = streamBuffersRef.current.get(responseId);
      const portalMeta = (streamBuffersRef.current as any).__portal?.get(responseId) as { messageId: string; agentId: string } | undefined;
      console.log(`[chat] response.done: responseId=${responseId} bufferSize=${buffer?.length ?? 0} types=[${buffer?.map((i: any) => i.type).join(',') ?? ''}] hasPortal=${!!portalMeta}`);
      if (buffer && buffer.length > 0 && portalMeta) {
        dispatch({ type: 'OVERLAY_DELTA', responseId, items: buffer, messageId: portalMeta.messageId, agentId: portalMeta.agentId });
      }

      // Clean up buffer and throttle state for this responseId
      streamBuffersRef.current.delete(responseId);
      (streamBuffersRef.current as any).__portal?.delete(responseId);
      dirtyResponseIds.current.delete(responseId);
      displayCursorsRef.current.delete(responseId);
      pauseFramesRef.current.delete(responseId);

      // Immediately mark the streaming placeholder as complete and clear overlay
      dispatch({ type: 'STREAM_COMPLETE', responseId });

      // Safety: refetch to sync final content from server
      sdbg(`unsubDone: scheduled fetchChat in 500ms responseId=${responseId} chatId=${chatId}`);
      setTimeout(() => {
        sdbg(`unsubDone: 500ms fetchChat firing responseId=${responseId} chatId=${chatId}`);
        fetchChatRef.current();
      }, 500);
    });

    // Insufficient balance to run the agent. A GUEST has no wallet yet — send
    // them to the signup gate ("create an account to chat with agents"); the
    // new account is granted demo credits, so the very next message goes
    // through. A real account that's actually out of funds gets the top-up
    // dialog (the engaged-user path).
    const unsubPayNotif = on('notification.new', (data: any) => {
      const n = data?.notification;
      if (n?.type === 'payment_required' && n?.data?.reason === 'insufficient_balance') {
        // `accountType` is narrowed to human|agent in the client User type, but
        // the runtime value IS 'guest' for ephemeral guest sessions (see
        // auth-provider) — read it through a cast.
        const isGuest = (user?.accountType as string | undefined) === 'guest';
        if (isGuest) {
          void requireSignup({ reason: 'Create a free account to chat with agents.', explicit: true });
        } else {
          setShowTopUpDialog(true);
        }
      }
    });

    return () => {
      holdTurn(null);
      unsubTyping?.(); unsubMessage?.(); unsubMessageUpdated?.(); unsubMessageDeleted?.();
      unsubOnline?.(); unsubOffline?.(); unsubAgentUnreachable?.();
      unsubDelta?.(); unsubError?.(); unsubCancelled?.(); unsubDone?.();
      unsubPayNotif?.();
      if (streamAnimationRef.current) { cancelAnimationFrame(streamAnimationRef.current); streamAnimationRef.current = null; }
    };
  }, [isConnected, chatId, user?.id, user?.accountType, requireSignup, on, clearTimers, holdTurn]);

  // Renderable document attachments stay inline as `DocumentChip`s in
  // the transcript. Earlier behaviour auto-`router.push`'d the first
  // such attachment on chat open, which navigated the user away from
  // `/workspace` (and any other chat embed). Removed — clicking the
  // chip still routes manually via its own `<a href>`.

  // ---- Input ----
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    const clamped = Math.min(el.scrollHeight, 180);
    el.style.height = `${clamped}px`;
    el.style.overflowY = clamped >= 180 ? 'auto' : 'hidden';
    if (!isDraft && chatId && isConnected && e.target.value.trim()) {
      if (!isTypingRef.current) { isTypingRef.current = true; sendTyping(chatId, true); }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => { isTypingRef.current = false; sendTyping(chatId!, false); }, 2000);
    } else if (!isDraft && chatId && isConnected && !e.target.value.trim() && isTypingRef.current) {
      isTypingRef.current = false; sendTyping(chatId, false);
    }
  };

  // ---- File handling ----
  const addFiles = useCallback((files: FileList | File[]) => {
    const snapshot = Array.from(files);
    if (snapshot.length === 0) return;
    setPendingFiles((prev) => [...prev, ...snapshot].slice(0, 10));
  }, []);

  const removeFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const pendingFileUrls = useMemo(() =>
    pendingFiles.map((f) =>
      f.type.startsWith('image/') || f.type.startsWith('video/')
        ? URL.createObjectURL(f) : null
    ),
    [pendingFiles]
  );
  useEffect(() => {
    return () => { pendingFileUrls.forEach((u) => { if (u) URL.revokeObjectURL(u); }); };
  }, [pendingFileUrls]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    const files: File[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const f = items[i].getAsFile();
        if (f) {
          const key = `${f.name}:${f.size}:${f.type}`;
          if (!seen.has(key)) { seen.add(key); files.push(f); }
        }
      }
    }
    if (files.length > 0) { e.preventDefault(); addFiles(files); }
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const uploadFiles = async (files: File[], targetChatId?: string): Promise<ContentItem[]> => {
    const items: ContentItem[] = [];
    let failedCount = 0;
    setUploading(true); setUploadProgress(0); setUploadError(null);
    const effectiveChatId = targetChatId || chatId;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const formData = new FormData();
        formData.append('file', file); if (effectiveChatId) formData.append('chatId', effectiveChatId); formData.append('visibility', 'chat');
        const res = await fetch('/api/content/upload', { method: 'POST', body: formData });
        if (res.ok) {
          const data = await res.json();
          const ct = data.contentType as string;
          if (ct === 'image') items.push({ type: 'image', image: { url: data.url }, alt_text: data.displayName, content_id: data.id, display_hint: 'inline' });
          else if (ct === 'video') items.push({ type: 'video', video: { url: data.url }, content_id: data.id, display_hint: 'inline' });
          else if (ct === 'audio') items.push({
            type: 'audio', audio: { url: data.url }, format: data.mimeType?.split('/')[1], content_id: data.id, display_hint: 'inline',
          } as AudioContent);
          else if (ct === 'model3d') items.push({
            type: 'file', file: { url: data.url },
            filename: data.displayName,
            mime_type: data.mimeType || 'model/gltf-binary',
            content_id: data.id,
            display_hint: 'inline',
          } as FileContent);
          else items.push({ type: 'file', file: { url: data.url }, filename: data.displayName, mime_type: data.mimeType, content_id: data.id, display_hint: 'attachment' });
        } else {
          failedCount++;
          console.error(`Upload failed for ${file.name}: ${res.status}`);
        }
      } catch (err) {
        failedCount++;
        console.error(`File upload failed (${file.name}):`, err);
      }
      setUploadProgress(Math.round(((i + 1) / files.length) * 100));
    }
    setUploading(false); setUploadProgress(0);
    if (failedCount > 0) {
      const msg = t('messages.uploadFailed', { count: failedCount });
      setUploadError(msg);
      setTimeout(() => setUploadError(null), 5000);
    }
    return items;
  };

  const openLightbox = useCallback((items: LightboxItem[], startIndex: number = 0) => {
    setLightboxItems(items); setLightboxIndex(startIndex);
  }, []);
  const closeLightbox = useCallback(() => { setLightboxItems([]); }, []);

  // Only available when this chat lives inside an AgentNode (canvas).
  // The button on each ImagePreview / VideoPreview only renders when
  // this handler is present — see `PersistedContentHandlers` above.
  const addMediaToCanvas = useMemo(() => {
    if (!hostingAgentItemId || !chatId) return undefined;
    return (
      contentId: string,
      meta?: { displayName?: string; mimeType?: string },
    ) => {
      emitWorkspaceSpawn({
        chatId,
        type: 'content',
        refId: contentId,
        cache: {
          displayName: meta?.displayName,
          mimeType: meta?.mimeType,
        },
      });
    };
  }, [hostingAgentItemId, chatId]);

  const persistedContentHandlersRef = useRef<PersistedContentHandlers>({ openLightbox, addMediaToCanvas });
  persistedContentHandlersRef.current.openLightbox = openLightbox;
  persistedContentHandlersRef.current.addMediaToCanvas = addMediaToCanvas;

  const getFileIcon = (file: File) => {
    const type = file.type;
    if (type.startsWith('image/')) return <ImageIcon className="h-4 w-4" />;
    if (type.startsWith('video/')) return <Film className="h-4 w-4" />;
    if (type.startsWith('audio/')) return <Music className="h-4 w-4" />;
    if (type.includes('gltf') || type.includes('usdz') || file.name.match(/\.(glb|gltf|usdz)$/i)) return <Box className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  // ---- Message editing ----
  const startEditMessage = useCallback((msg: Message) => {
    const text = msg.content || msg.contentItems?.filter(i => i.type === 'text' && 'text' in i).map(i => (i as TextContent).text).join('\n') || '';
    setEditingMessageId(msg.id); setEditContent(text);
    setTimeout(() => editTextareaRef.current?.focus(), 50);
  }, []);
  const cancelEditMessage = useCallback(() => { setEditingMessageId(null); setEditContent(''); }, []);

  const saveEditMessage = useCallback(async () => {
    if (!editingMessageId || !editContent.trim()) return;
    const newContent = editContent.trim();
    const msgId = editingMessageId;
    dispatch({ type: 'MESSAGE_EDITED', messageId: msgId, content: newContent });
    setEditingMessageId(null); setEditContent('');
    try {
      const res = await fetch(`/api/messages/${chatId}/${msgId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newContent }),
      });
      if (!res.ok) { console.error('Failed to save edit'); await fetchChat(); }
    } catch (err) { console.error('Error saving edit:', err); await fetchChat(); }
  }, [editingMessageId, editContent, chatId, fetchChat]);

  const handleDeleteMessage = useCallback(async (msgId: string) => {
    if (!window.confirm(t('messages.confirmDelete'))) return;
    dispatch({ type: 'MESSAGE_DELETED', messageId: msgId });
    try {
      const res = await fetch(`/api/messages/${chatId}/${msgId}`, { method: 'DELETE' });
      if (!res.ok) { console.error('Failed to delete message'); await fetchChat(); }
    } catch (err) { console.error('Error deleting message:', err); await fetchChat(); }
  }, [chatId, fetchChat, t]);

  // ---- Send ----
  const handleSend = async () => {
    if ((!input.trim() && pendingFiles.length === 0) || sending || uploading) return;
    haptic('light');
    const content = input.trim();
    const filesToUpload = [...pendingFiles];
    setInput(''); setPendingFiles([]); setSending(true);
    // Re-measure after React commits the empty `input`. Setting
    // `height = 'auto'` synchronously here misses because the textarea
    // still holds the tall stale value on this tick; `resizeTextarea`
    // needs the post-commit DOM to compute the correct clamped height.
    requestAnimationFrame(() => resizeTextarea());
    if (!isDraft && chatId && isTypingRef.current) { isTypingRef.current = false; if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current); sendTyping(chatId, false); }

    // Draft mode: create chat first, then upload files into it, then send message
    if (isDraft && otherParticipant) {
      try {
        // If no files, use the atomic create-with-message endpoint
        if (filesToUpload.length === 0) {
          const body: Record<string, unknown> = { type: 'dm', participantIds: [otherParticipant.userId] };
          if (content) body.content = content;
          const res = await fetch('/api/messages/create-with-message', {
            method: 'POST', headers: sendHeaders, body: JSON.stringify(body),
          });
          if (res.ok) {
            const data = await res.json();
            trackChatSend(data.chatId);
            window.dispatchEvent(new CustomEvent('chatCreated', { detail: { chatId: data.chatId } }));
            onChatCreated?.(data.chatId);
          } else {
            setInput(content);
            // A refusal here is usually the recipient's DM setting, and
            // the server phrases that better than we can. Surface it
            // instead of dropping the text back in the box with no
            // explanation for why nothing happened.
            setSendError(await readChatError(res));
            console.error(`Failed to create chat: ${res.status}`);
          }
          return;
        }

        // With files: create chat → upload into it → send message
        const chatRes = await fetch('/api/messages', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'dm', participantIds: [otherParticipant.userId] }),
        });
        if (!chatRes.ok) {
          setInput(content);
          setSendError(await readChatError(chatRes));
          console.error(`Failed to create chat: ${chatRes.status}`);
          return;
        }
        const { chatId: newChatId } = await chatRes.json();

        const fileContentItems = await uploadFiles(filesToUpload, newChatId);
        if (fileContentItems.length === 0 && !content) return;

        const contentItems: ContentItem[] = [];
        if (content) contentItems.push({ type: 'text', text: content });
        contentItems.push(...fileContentItems);

        const msgRes = await fetch(`/api/messages/${newChatId}`, {
          method: 'POST', headers: sendHeaders,
          body: JSON.stringify({ content: content || undefined, contentItems }),
        });
        if (msgRes.ok) {
          trackChatSend(newChatId);
          window.dispatchEvent(new CustomEvent('chatCreated', { detail: { chatId: newChatId } }));
          onChatCreated?.(newChatId);
        } else {
          setInput(content);
          console.error(`Failed to send message: ${msgRes.status}`);
        }
      } catch (error) {
        console.error('Error creating chat with message:', error);
        setInput(content);
      } finally {
        setSending(false);
        // Refocus the textarea after the draft → chat transition so the user
        // can keep typing without clicking back into the input.
        requestAnimationFrame(() => inputRef.current?.focus());
      }
      return;
    }

    let fileContentItems: ContentItem[] = [];
    if (filesToUpload.length > 0) fileContentItems = await uploadFiles(filesToUpload);
    if (filesToUpload.length > 0 && fileContentItems.length === 0 && !content) {
      setSending(false);
      return;
    }
    const contentItems: ContentItem[] = [];
    if (content) contentItems.push({ type: 'text', text: content });
    contentItems.push(...fileContentItems);

    const optimisticMessage: Message = {
      id: `temp-${Date.now()}`, chatId: chatId!, senderId: user!.id, senderUsername: user!.username,
      senderAvatar: user!.avatarUrl || undefined,
      senderType: user!.accountType as 'human' | 'agent',
      content: content || undefined,
      contentItems: fileContentItems.length > 0 ? contentItems : undefined,
      role: 'user', senderKind: 'human', createdAt: new Date().toISOString(),
    };
    dispatch({ type: 'OPTIMISTIC_SEND', message: optimisticMessage });
    requestAnimationFrame(() => stickyInstance.scrollToBottom());
    setSendError(null);

    try {
      const body: Record<string, unknown> = {};
      if (content) body.content = content;
      if (fileContentItems.length > 0) body.contentItems = contentItems;
      // For bridged chats the toggle is the mode switch (true = send to the
      // platform + agent silent; false = stay in portal + agent replies),
      // so we always send the explicit boolean. For non-bridged chats the
      // toggle isn't rendered and the field stays absent so the server
      // applies its normal defaults.
      const sourceRaw = chat?.metadata?.source;
      const sourceStr = typeof sourceRaw === 'string' ? sourceRaw : undefined;
      const isBridgedChat =
        chat?.type === 'dm' && !!sourceStr && OWNER_OUTBOUND_PROVIDERS.has(sourceStr);
      if (isBridgedChat) body.bridgeOutbound = bridgeOutbound;
      const res = await fetch(`/api/messages/${chatId}`, {
        method: 'POST', headers: sendHeaders, body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        if (chatId) trackChatSend(chatId);
        dispatch({ type: 'OPTIMISTIC_CONFIRM', tempId: optimisticMessage.id, message: data.message });
      } else {
        dispatch({ type: 'OPTIMISTIC_FAIL', tempId: optimisticMessage.id });
        setSendError(await readChatError(res));
        console.error(`Failed to send message: ${res.status} ${res.statusText}`);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      dispatch({ type: 'OPTIMISTIC_FAIL', tempId: optimisticMessage.id });
      setSendError(!navigator.onLine ? 'You\'re offline. Check your connection.' : 'Failed to send. Tap your message to retry.');
    } finally {
      setSending(false); inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const handleRetry = async (msgId: string) => {
    const failedMsg = messages.find(m => m.id === msgId);
    if (!failedMsg || !chatId) return;
    const content = failedMsg.content || failedMsg.contentItems?.find(i => i.type === 'text' && 'text' in i)?.text || '';
    if (!content) return;
    dispatch({ type: 'MESSAGE_DELETED', messageId: msgId });
    const optimisticMessage: Message = {
      id: `temp-${Date.now()}`, chatId, senderId: user!.id, senderUsername: user!.username,
      senderAvatar: user!.avatarUrl || undefined, senderType: user!.accountType as 'human' | 'agent',
      content, role: 'user', senderKind: 'human', createdAt: new Date().toISOString(),
    };
    dispatch({ type: 'OPTIMISTIC_SEND', message: optimisticMessage });
    requestAnimationFrame(() => stickyInstance.scrollToBottom());
    setSendError(null);
    try {
      const sourceRaw = chat?.metadata?.source;
      const sourceStr = typeof sourceRaw === 'string' ? sourceRaw : undefined;
      const isBridgedChat =
        chat?.type === 'dm' && !!sourceStr && OWNER_OUTBOUND_PROVIDERS.has(sourceStr);
      const res = await fetch(`/api/messages/${chatId}`, {
        method: 'POST', headers: sendHeaders,
        body: JSON.stringify({ content, ...(isBridgedChat ? { bridgeOutbound } : {}) }),
      });
      if (res.ok) {
        const data = await res.json();
        dispatch({ type: 'OPTIMISTIC_CONFIRM', tempId: optimisticMessage.id, message: data.message });
      } else {
        dispatch({ type: 'OPTIMISTIC_FAIL', tempId: optimisticMessage.id });
        setSendError('Failed to send. Tap your message to retry.');
      }
    } catch {
      dispatch({ type: 'OPTIMISTIC_FAIL', tempId: optimisticMessage.id });
      setSendError(!navigator.onLine ? 'You\'re offline. Check your connection.' : 'Failed to send. Tap your message to retry.');
    }
  };

  // ---- Chat name editing ----
  const handleStartEditName = () => { if (!chat) return; setEditedName(chat.name || ''); setEditingName(true); setTimeout(() => nameInputRef.current?.focus(), 50); };
  const handleSaveName = async () => {
    if (!chat || savingName) return;
    setSavingName(true);
    try {
      const res = await fetch(`/api/messages/${chatId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: editedName.trim() || null }) });
      if (res.ok) { setChat((prev) => prev ? { ...prev, name: editedName.trim() || undefined } : null); setEditingName(false); }
    } catch (error) { console.error('Error saving chat name:', error); }
    finally { setSavingName(false); }
  };
  const handleNameKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveName(); } else if (e.key === 'Escape') setEditingName(false); };

  // ---- User management ----
  useEffect(() => {
    if (!addUserSearch.trim() || !showAddUser) { setAddUserResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const [agentsRes, humansRes] = await Promise.all([
          fetch(`/api/discovery/agents?search=${encodeURIComponent(addUserSearch)}&limit=5&type=agent`),
          fetch(`/api/discovery/agents?search=${encodeURIComponent(addUserSearch)}&limit=5&type=human`),
        ]);
        const [agentsData, humansData] = await Promise.all([agentsRes.json(), humansRes.json()]);
        const existingIds = new Set([user?.id, ...(chat?.participants.map(p => p.userId) || [])]);
        const combined = [
          ...(humansData.agents || []).map((u: any) => ({ id: u.id, username: u.username, displayName: u.displayName, avatarUrl: u.avatarUrl, accountType: 'human' as const })),
          ...(agentsData.agents || []).map((a: any) => ({ id: a.id, username: a.username, displayName: a.displayName, avatarUrl: a.avatarUrl, accountType: 'agent' as const })),
        ].filter((u) => !existingIds.has(u.id));
        setAddUserResults(combined);
      } catch (error) { console.error('Error searching users:', error); }
    }, 300);
    return () => clearTimeout(timer);
  }, [addUserSearch, showAddUser, user?.id, chat?.participants]);

  const handleAddUser = async (userId: string) => {
    if (!chat || addingUser) return; setAddingUser(true);
    try {
      const res = await fetch(`/api/messages/${chatId}/participants`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId }) });
      if (res.ok) { await fetchChat(); setShowAddUser(false); setAddUserSearch(''); setAddUserResults([]); }
    } catch (error) { console.error('Error adding user:', error); }
    finally { setAddingUser(false); }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!chat || removingUserId) return; setRemovingUserId(userId);
    try {
      const res = await fetch(`/api/messages/${chatId}/participants`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId }) });
      if (res.ok) await fetchChat();
    } catch (error) { console.error('Error removing user:', error); }
    finally { setRemovingUserId(null); }
  };

  // ---- Memoized display pipeline ----
  // Hide persisted tool-turn rows from the visible transcript. These rows
  // (assistant turns with `metadata.kind = 'tool_turn'` and `role = 'tool'`
  // result rows) are written by `recordToolTurn` so the LLM sees full prior
  // tool history on reload — they are NOT meant for the user-facing chat.
  // Without this filter every persisted tool round shows up as standalone
  // bubbles after the main message; see plans/hide_persisted_tool-turn_*.
  // The agent's final summary message is written by a different path (no
  // `kind: 'tool_turn'` marker) and remains visible — and `present()`'s
  // file/image content_items live on that summary message, so the user
  // still sees the rendered artifact.
  const displayMessages = useMemo(() => filterDisplayMessages(messages), [messages]);

  const messageGroups = useMemo(() => groupMessages(displayMessages), [displayMessages]);

  // ---- Early returns ----
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="2xl" tone="muted" />
      </div>
    );
  }
  // `chat` is the shell for every live chatId (see `shellChat`), so this is
  // null only with neither a chatId nor a draft: never a loaded-history gate.
  if (!user || !chat) return null;

  // ---- Render ----
  return (
    <ChatHostProvider
      value={{
        onOpenContent,
        onOpenProfile,
        onReplayDelegation,
        // Standalone fallback for the app-building-off card: outside the
        // canvas the only surface that mounts AgentSettingsPanel is the
        // workspace agent tile, so land the user on it.
        onOpenAgentSettings:
          onOpenAgentSettings ??
          (isAgent && otherParticipant
            ? () => {
                router.push(`/workspace?chat=${encodeURIComponent(otherParticipant.username)}`);
                return true;
              }
            : undefined),
        hostingAgentItemId,
        rootHostingAgentItemId,
        chatId,
      }}
    >
    <div
      className={cn(
        "flex flex-col bg-background relative",
        embedded ? "h-full" : "h-[calc(100dvh-3rem)] -mx-4 -my-4 md:-my-6"
      )}
      data-testid="chat-view"
      data-chat-id={chatId ?? undefined}
      data-chat-mode={isDraft ? 'draft' : 'live'}
      data-history-state={isDraft ? undefined : historyState}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="absolute inset-0 z-50 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-primary">
            <Upload className="h-10 w-10" />
            <span className="font-medium text-sm">{t('messages.dropFilesToUpload')}</span>
          </div>
        </div>
      )}

      {/* Header */}
      {!hideHeader && (
      <div className="shrink-0 p-3 border-b border-border flex items-center gap-3">
        <div className="relative shrink-0">
          {chat.type === 'group' ? (
            <div className="relative h-10 w-10">
              {(() => {
                const others = chat.participants.filter(p => p.userId !== user?.id);
                const shown = others.slice(0, 2);
                const extra = others.length - shown.length;
                if (shown.length === 0) return (
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                    <Users className="h-5 w-5 text-muted-foreground" />
                  </div>
                );
                if (shown.length === 1) return (
                  <Avatar seed={shown[0].username} type={shown[0].accountType} src={shown[0].avatarUrl} className="h-10 w-10" />
                );
                return (
                  <>
                    {shown.map((p, i) => (
                      <Avatar key={p.userId} seed={p.username} type={p.accountType} src={p.avatarUrl} className={cn('absolute h-7 w-7 ring-2 ring-background', i === 0 ? 'top-0 left-0 z-10' : 'bottom-0 right-0')} />
                    ))}
                    {extra > 0 && (
                      <div className="absolute -bottom-0.5 -right-0.5 z-20 h-4 w-4 rounded-full bg-muted ring-1 ring-background flex items-center justify-center">
                        <span className="text-[8px] font-bold text-muted-foreground">+{extra}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <Link href={otherParticipant ? `/u/${otherParticipant.username}` : '#'}>
              <Avatar seed={otherParticipant?.username || displayName} type={isAgent ? 'agent' : 'human'} src={chat.avatarUrl || otherParticipant?.avatarUrl} className="h-10 w-10" />
            </Link>
          )}
          {chat.type !== 'group' && otherParticipant?.isOnline && (
            <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-500 border-[2.5px] border-background" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          {chat.type === 'group' ? (
            editingName ? (
              <div className="flex items-center gap-2">
                <input ref={nameInputRef} type="text" value={editedName} onChange={(e) => setEditedName(e.target.value)} onKeyDown={handleNameKeyDown} placeholder={t('messages.chatNameInputPlaceholder')} className="input h-8 flex-1 text-sm" maxLength={100} />
                <button onClick={handleSaveName} disabled={savingName} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-accent text-primary">
                  {savingName ? <Spinner size="md" /> : <Check className="h-4 w-4" />}
                </button>
                <button onClick={() => setEditingName(false)} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-accent text-muted-foreground"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <h1 className="font-medium text-sm truncate flex items-center gap-1.5">
                    <span className="truncate">{chat.name || t('messages.groupChat')}</span>
                    <ChatSourceBadge source={typeof chat.metadata?.source === 'string' ? chat.metadata.source : undefined} />
                  </h1>
                  {/* TEMPORARY: group chats disabled — re-enable when ready
                  <button onClick={() => setShowAddUser(v => !v)} className="text-xs text-muted-foreground truncate block max-w-full text-left hover:text-foreground transition-colors">
                    {t('messages.participantsCount', { count: chat.participants.length })}
                  </button>
                  */}
                  <span className="text-xs text-muted-foreground truncate block max-w-full">
                    {t('messages.participantsCount', { count: chat.participants.length })}
                  </span>
                </div>
                {canEditChat && (
                  <Tip label={t('messages.editChatName')}>
                  <button onClick={handleStartEditName} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-accent text-muted-foreground hover:text-foreground transition-colors shrink-0">
                    <Pencil className="h-4 w-4" />
                  </button>
                  </Tip>
                )}
              </div>
            )
          ) : editingName ? (
            <div className="flex items-center gap-2">
              <input ref={nameInputRef} type="text" value={editedName} onChange={(e) => setEditedName(e.target.value)} onKeyDown={handleNameKeyDown} placeholder={t('messages.chatNameInputPlaceholder')} className="input h-8 flex-1 text-sm" maxLength={100} />
              <button onClick={handleSaveName} disabled={savingName} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-accent text-primary">
                {savingName ? <Spinner size="md" /> : <Check className="h-4 w-4" />}
              </button>
              <button onClick={() => setEditingName(false)} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-accent text-muted-foreground"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link href={otherParticipant ? `/u/${otherParticipant.username}` : '#'} className="block min-w-0 flex-1">
                {chat.name ? (
                  <>
                    <h1 className="font-medium text-sm truncate flex items-center gap-1.5">
                      <span className="truncate">{chat.name}</span>
                      <ChatSourceBadge source={typeof chat.metadata?.source === 'string' ? chat.metadata.source : undefined} />
                    </h1>
                    {otherParticipant && <p className={cn("text-xs truncate", isAgent ? "text-purple-500" : "text-emerald-500")}>@{otherParticipant.username}</p>}
                  </>
                ) : (
                  <>
                    <h1 className="font-semibold text-sm truncate flex items-center gap-1.5">
                      <span className="truncate">{otherParticipant?.displayName || otherParticipant?.username || displayName}</span>
                      <ChatSourceBadge source={typeof chat.metadata?.source === 'string' ? chat.metadata.source : undefined} />
                    </h1>
                    {otherParticipant && <p className={cn("text-xs truncate", isAgent ? "text-purple-500" : "text-emerald-500")}>@{otherParticipant.username}</p>}
                  </>
                )}
              </Link>
              {canEditChat && (
                <Tip label={t('messages.editChatName')}>
                <button onClick={handleStartEditName} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-accent text-muted-foreground hover:text-foreground transition-colors shrink-0">
                  <Pencil className="h-4 w-4" />
                </button>
                </Tip>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-0.5 shrink-0 relative">
          <Tip label={t('messages.addToChat')}>
          <button onClick={() => setShowAddUser(!showAddUser)} className={cn("h-8 w-8 rounded-lg flex items-center justify-center transition-colors", showAddUser ? "bg-primary text-primary-foreground" : "hover:bg-accent text-muted-foreground hover:text-foreground")}>
            <Plus className="h-4 w-4" />
          </button>
          </Tip>
          <Tip label={t('chatSidebar.backToList')}>
          <button onClick={handleBack} aria-label={t('chatView.backToList')} className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-accent transition-colors text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
          </Tip>

          {showAddUser && (
            <div className="absolute top-full right-0 mt-2 w-80 bg-background border border-border rounded-lg shadow-lg z-50 overflow-hidden">
              <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('messages.participantsPanel', { count: chat.participants.length })}</span>
              </div>
              <div className="max-h-52 overflow-y-auto">
                {chat.participants.map((p) => {
                  const pIsAgent = p.accountType === 'agent';
                  const isSelf = p.userId === user?.id;
                  // TEMPORARY: group chats disabled — re-enable when ready
                  // const canRemove = chat.type === 'group' && chat.participants.length > 2 && (canEditChat || isSelf) && !isSelf;
                  // const canLeave = chat.type === 'group' && chat.participants.length > 2 && isSelf;
                  const canRemove = false;
                  const canLeave = false;
                  return (
                    <div key={p.userId} className="flex items-center gap-2.5 px-3 py-1.5 group/participant hover:bg-accent/50 transition-colors">
                      <Link href={`/u/${p.username}`} className="flex items-center gap-2.5 flex-1 min-w-0">
                        <Avatar seed={p.username} type={p.accountType} src={p.avatarUrl} className="h-7 w-7" />
                        <div className="flex-1 min-w-0">
                          <span className={cn("text-sm font-medium truncate block", pIsAgent ? "text-purple-500" : "text-emerald-500")}>@{p.username}</span>
                        </div>
                      </Link>
                      {isSelf && <span className="text-[10px] text-muted-foreground/60 shrink-0">{t('messages.you')}</span>}
                      {(canRemove || canLeave) && (
                        <Tip label={canLeave ? t('messages.leaveChat') : t('messages.removeFromChat')}>
                        <button onClick={() => handleRemoveUser(p.userId)} disabled={removingUserId === p.userId} className="h-5 w-5 rounded flex items-center justify-center text-muted-foreground/40 hover:text-red-500 hover:bg-red-500/10 transition-colors opacity-0 group-hover/participant:opacity-100 shrink-0">
                          {removingUserId === p.userId ? <Spinner size="xs" /> : <X className="h-3 w-3" />}
                        </button>
                        </Tip>
                      )}
                    </div>
                  );
                })}
              </div>
              {canAddParticipants && (
              <div className="border-t border-border p-2">
                <input type="text" placeholder={t('messages.searchPeople')} value={addUserSearch} onChange={(e) => setAddUserSearch(e.target.value)} className="input h-8 w-full text-sm" autoFocus />
              </div>
              )}
              {addUserResults.length > 0 && (
                <div className="max-h-40 overflow-y-auto border-t border-border">
                  {addUserResults.map((result) => {
                    const isResultAgent = result.accountType === 'agent';
                    return (
                      <button key={result.id} onClick={() => handleAddUser(result.id)} disabled={addingUser} className="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-accent transition-colors text-left disabled:opacity-50">
                        <Avatar seed={result.username} type={result.accountType} src={result.avatarUrl} className="h-7 w-7" />
                        <div className="flex-1 min-w-0">
                          <span className={cn('text-sm font-medium truncate block', isResultAgent ? 'text-purple-500' : 'text-emerald-500')}>@{result.username}</span>
                        </div>
                        <Plus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
              {addUserSearch && addUserResults.length === 0 && (
                <div className="p-3 text-xs text-muted-foreground text-center border-t border-border">{t('messages.noUsersFound')}</div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Messages */}
      <StickToBottom instance={stickyInstance} className="flex-1 relative min-h-0">
      <StickToBottom.Content className="p-4 space-y-3 flex flex-col min-h-full">
        {/* Initial-history status once the composer is up (2026-09-06): a
            spinner row while the ladder in fetchChat is still trying, and a
            stated failure with a retry once it is spent. Draft chats have no
            history to load. The strings are literals like the send-error
            copy below; en.json has no fitting key yet. */}
        {!isDraft && !loadedChat && historyState === 'pending' && (
          <div className="flex justify-center py-2"><Spinner size="lg" tone="muted" /></div>
        )}
        {!isDraft && historyState === 'failed' && (
          <div className="flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground" data-testid="chat-history-failed">
            <AlertCircle className="h-3 w-3 shrink-0 opacity-50" />
            <span>Earlier messages could not be loaded.</span>
            <button type="button" onClick={retryHistory} className="inline-flex items-center gap-1 text-primary hover:underline">
              <RefreshCw className="h-3 w-3" /> {t('messages.tryAgain')}
            </button>
          </div>
        )}
        {loadingMore && (
          <div className="flex justify-center py-2"><Spinner size="lg" tone="muted" /></div>
        )}
        {hasMore && !loadingMore && messages.length > 0 && (
          <div className="flex justify-center py-2"><span className="text-xs text-muted-foreground">{t('messages.scrollForMore')}</span></div>
        )}
        {messages.length === 0 && !loading && agentGreeting && (
          <div className="flex flex-col items-center justify-center text-center flex-1 min-h-0 w-full">
            {agentGreeting.heroImage ? (
              <div className="w-full max-w-2xl px-4 mb-5">
                <div className="rounded-2xl overflow-hidden"><Image src={agentGreeting.heroImage} alt="" width={800} height={420} unoptimized className="w-full h-auto object-cover" sizes="(max-width: 768px) 100vw, 672px" /></div>
              </div>
            ) : otherParticipant ? (
              <Avatar seed={otherParticipant.username} type="agent" src={otherParticipant.avatarUrl} className="h-[4.25rem] w-[4.25rem] mb-5" />
            ) : null}
            <div className="max-w-[350px]">
              {/* The name shows only on the FALLBACK screen (no configured
                  greeting): an agent whose owner wrote a greeting gets the
                  greeting alone, exactly as before. */}
              {agentGreeting.displayName && <p className="text-sm font-medium mb-1">{agentGreeting.displayName}</p>}
              {agentGreeting.greetingMessage
                ? <p className="text-sm text-muted-foreground mb-4">{agentGreeting.greetingMessage}</p>
                : agentGreeting.displayName
                  ? <p className="text-sm text-muted-foreground mb-4">{t('messages.agentChatStart', { name: agentGreeting.displayName })}</p>
                  : null}
            </div>
            {agentGreeting.greetingPrompt && (
              <p className="text-sm font-medium text-purple-500 mb-8">{agentGreeting.greetingPrompt}</p>
            )}
            {agentGreeting.suggestedActions && agentGreeting.suggestedActions.length > 0 && (
              <div className="flex flex-col items-center gap-2.5">
                {agentGreeting.suggestedActions.map((raw, i) => {
                  const hashIdx = raw.indexOf('#');
                  const label = hashIdx >= 0 ? raw.slice(0, hashIdx) : raw;
                  const prompt = hashIdx >= 0 ? raw.slice(hashIdx + 1) : raw;
                  return (
                    <button key={i} onClick={() => { haptic('selection'); setInput(prompt); requestAnimationFrame(resizeTextarea); inputRef.current?.focus(); }} className="px-6 py-2 rounded-full border border-border text-sm hover:bg-accent transition-colors">{label}</button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {messageGroups.map((group) => {
          const isOwn = group.senderId === user.id;
          const isSenderAgent = group.senderType === 'agent';
          const isStreaming = group.messages.some(m => m.metadata?.status === 'streaming');

          return (
            <div key={group.groupKey}
              data-testid={isSenderAgent ? 'assistant-message' : undefined}
              data-message-status={isSenderAgent ? (isStreaming ? 'streaming' : 'complete') : undefined}
              className={cn('flex gap-2 group chat-message-enter', isOwn && 'flex-row-reverse')}>
              {!isOwn && (
                <div className="w-8 shrink-0">
                  <Link
                    href={`/u/${group.senderUsername}`}
                    aria-label={group.senderUsername}
                    className="block cursor-pointer transition-transform hover:scale-105"
                  >
                    <Avatar seed={group.senderUsername} type={group.senderType} src={group.senderAvatar} className="h-8 w-8" />
                  </Link>
                </div>
              )}

              <div className={cn('max-w-full @md:max-w-[80%] min-w-0 flex flex-col', isOwn ? 'items-end' : 'items-start')}>
                {!isOwn && (
                  <Link
                    href={`/u/${group.senderUsername}`}
                    className={cn(
                      'text-[11px] font-medium mb-0.5 px-1 cursor-pointer hover:underline transition-colors',
                      isSenderAgent
                        ? 'text-purple-500 hover:text-purple-400'
                        : 'text-emerald-500 hover:text-emerald-400',
                    )}
                  >
                    {chat.participants.find(p => p.userId === group.senderId)?.displayName || group.senderUsername}
                  </Link>
                )}
                {group.messages.map((message, msgIdx) => {
                  const isFirst = msgIdx === 0;
                  const isLast = msgIdx === group.messages.length - 1;
                  const isEditing = editingMessageId === message.id;
                  const msgStatus = message.metadata?.status as string | undefined;
                  const overlay = (() => {
                    if (message.uampEventId) {
                      const o = overlays.get(message.uampEventId);
                      if (o) return o;
                    }
                    for (const [, o] of overlays) {
                      if (o.messageId === message.id) return o;
                    }
                    return undefined;
                  })();

                  // Streaming placeholder with active overlay: render overlay content
                  if (overlay && overlay.items.length > 0) {
                    const overlayItems = overlay.items;
                    // When the request is aborted/errored, freeze all in-progress
                    // animations (chip pulse, dot pulse, title shimmer).
                    const isInactive = msgStatus === 'cancelled' || msgStatus === 'error';
                    return (
                      <div key={message.id} className={cn('relative max-w-full', !isFirst && 'mt-0.5')} style={{ willChange: 'contents' }}>
                        <div className={cn(
                          'px-4 py-2 max-w-full overflow-hidden bg-muted',
                          isFirst && isLast && 'rounded-2xl rounded-bl-md',
                          isFirst && !isLast && 'rounded-t-2xl rounded-br-2xl rounded-bl-md',
                          !isFirst && isLast && 'rounded-b-2xl rounded-tr-2xl rounded-tl-md',
                          !isFirst && !isLast && 'rounded-r-2xl rounded-l-md',
                        )}>
                          <SmoothShrink>
                            <div className="space-y-1 min-w-0 overflow-hidden">
                            {(() => {
                              type OverlayGroup =
                                | { kind: 'tool_run'; key: string; items: ToolStripItem[]; textStreamingAfter: boolean }
                                | { kind: 'messaging_bubble'; key: string; toolName: string; args?: string; result?: string; isStreaming: boolean; isError?: boolean }
                                | { kind: 'item'; key: string; idx: number; item: typeof overlayItems[number] };
                              const groups: OverlayGroup[] = [];
                              let runItems: ToolStripItem[] | null = null;
                              let runKey = '';
                              const flushRun = (textStreamingAfter: boolean) => {
                                if (runItems) {
                                  groups.push({ kind: 'tool_run', key: runKey, items: runItems, textStreamingAfter });
                                  runItems = null;
                                }
                              };
                              for (let i = 0; i < overlayItems.length; i++) {
                                const it = overlayItems[i];
                                if (it.type === 'tool_call') {
                                  if (it.name === 'present' || it.name.startsWith('present.')) continue;
                                  // Messaging-platform send tools render as a top-level
                                  // bubble in the body, not as a chip inside the
                                  // collapsible tool-call accordion. See chat-view
                                  // grouping for the persisted-message branch above.
                                  if (isMessagingSendTool(it.name)) {
                                    flushRun(true);
                                    groups.push({
                                      kind: 'messaging_bubble',
                                      key: `bubble-${it.id}`,
                                      toolName: it.name,
                                      args: it.arguments,
                                      result: it.result,
                                      isStreaming: !it.result && !isInactive,
                                    });
                                    continue;
                                  }
                                  const nonThinkingSegs = (it.progressSegments ?? []).filter(s => s.kind !== 'thinking');
                                  const progressText = nonThinkingSegs.map(s => s.text).join('');
                                  const lastReplaceText = [...nonThinkingSegs].reverse().find(s => s.kind === 'replace')?.text;
                                  const stripItem: ToolStripItem = {
                                    id: it.id,
                                    kind: 'tool',
                                    name: it.name,
                                    args: it.arguments,
                                    result: it.result || progressText,
                                    data: it.data,
                                    progressKey: lastReplaceText,
                                    isStreaming: !it.result && !isInactive,
                                    progressSegments: it.progressSegments,
                                    awaitingApproval: !!it.awaitingApproval && !it.result,
                                  };
                                  if (runItems) runItems.push(stripItem);
                                  else { runItems = [stripItem]; runKey = `run-${stripItem.id}`; }
                                  continue;
                                }
                                if (it.type === 'thinking') {
                                  // Treat thinking as a chip in the run; mark as
                                  // streaming if it is the trailing item and the
                                  // request is still active.
                                  const isLastItem = i === overlayItems.length - 1;
                                  const stripItem: ToolStripItem = {
                                    id: `think-${i}`,
                                    kind: 'thinking',
                                    name: 'thinking',
                                    text: it.text,
                                    isStreaming: isLastItem && !isInactive,
                                  };
                                  if (runItems) runItems.push(stripItem);
                                  else { runItems = [stripItem]; runKey = `run-think-${i}`; }
                                  continue;
                                }
                                // Skip empty text segments — they must not break the
                                // chip row into a second row, and must not auto-close
                                // the details panel.
                                if (it.type === 'text' && !it.text.trim()) continue;
                                // Non-tool/non-thinking item: meaningful text/file
                                // signals "next thing started streaming".
                                const nextIsTextish = it.type === 'text' || it.type === 'file';
                                flushRun(nextIsTextish);
                                groups.push({ kind: 'item', key: `${it.type}-${i}`, idx: i, item: it });
                              }
                              flushRun(false);

                              return groups.map((group): ReactNode => {
                                if (group.kind === 'tool_run') {
                                  return (
                                    <ToolCallStrip
                                      key={group.key}
                                      items={group.items}
                                      textStreamingAfter={group.textStreamingAfter}
                                    />
                                  );
                                }
                                if (group.kind === 'messaging_bubble') {
                                  const props = getMessagingSendBubbleProps(
                                    group.toolName,
                                    group.args,
                                    group.result,
                                    group.isError,
                                    group.isStreaming,
                                  );
                                  return props ? <MessagingSendBubble key={group.key} {...props} /> : null;
                                }
                                const { item, idx } = group;
                                if (item.type === 'thinking') {
                                  const isLastItem = idx === overlayItems.length - 1;
                                  return <ThinkingBlock key={`thinking-${idx}`} content={item.text} isStreaming={isLastItem} />;
                                }
                                if (item.type === 'file') {
                                  const meta = item.metadata;
                                  return <DocumentChip key={`file-${idx}`} contentId={item.content_id} filename={item.filename} mimeType={item.mime_type} command={meta?.command as string | undefined} diff={meta ? { old_str: meta.old_str as string | undefined, new_str: meta.new_str as string | undefined, insert_line: meta.insert_line as number | undefined } : undefined} />;
                                }
                                if (item.type === 'system_notice') {
                                  return (
                                    <div
                                      key={`notice-${idx}`}
                                      className="mt-1 mb-1 px-3 py-2 text-xs rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-200 whitespace-pre-wrap"
                                    >
                                      {item.text}
                                    </div>
                                  );
                                }
                                if (item.type === 'text') {
                                  return (
                                    <div key={idx} className="text-sm break-words prose prose-sm dark:prose-invert max-w-none min-w-0 overflow-hidden">
                                      <Markdown suppressLinkPreviews>{stripContentLabels(item.text)}</Markdown>
                                    </div>
                                  );
                                }
                                return null;
                              });
                            })()}
                            </div>
                          </SmoothShrink>
                        </div>
                        {msgStatus === 'streaming' && (
                          <div className="flex items-center gap-1 px-1 pt-2.5">
                            <span className="thinking-dot h-1 w-1 rounded-full bg-muted-foreground" style={{ animationDelay: '0ms' }} />
                            <span className="thinking-dot h-1 w-1 rounded-full bg-muted-foreground" style={{ animationDelay: '150ms' }} />
                            <span className="thinking-dot h-1 w-1 rounded-full bg-muted-foreground" style={{ animationDelay: '300ms' }} />
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Streaming placeholder with no overlay yet: show thinking dots
                  if (msgStatus === 'streaming' && !overlay) {
                    return (
                      <div key={message.id} className={cn('relative max-w-full chat-message-enter', !isFirst && 'mt-0.5')}>
                        <div className="flex items-center gap-1 px-1 pt-2.5">
                          <span className="thinking-dot h-1 w-1 rounded-full bg-muted-foreground" style={{ animationDelay: '0ms' }} />
                          <span className="thinking-dot h-1 w-1 rounded-full bg-muted-foreground" style={{ animationDelay: '150ms' }} />
                          <span className="thinking-dot h-1 w-1 rounded-full bg-muted-foreground" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    );
                  }

                  if (message.isDeleted) {
                    return (
                      <div key={message.id} className={cn('relative group/msg max-w-full', !isFirst && 'mt-0.5')}>
                        <div className={cn(
                          'px-4 py-2 max-w-full overflow-hidden',
                          isOwn ? 'bg-primary/5' : 'bg-muted/50',
                          isFirst && isLast && (isOwn ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-bl-md'),
                          isFirst && !isLast && (isOwn ? 'rounded-t-2xl rounded-bl-2xl rounded-br-md' : 'rounded-t-2xl rounded-br-2xl rounded-bl-md'),
                          !isFirst && isLast && (isOwn ? 'rounded-b-2xl rounded-tl-2xl rounded-tr-md' : 'rounded-b-2xl rounded-tr-2xl rounded-tl-md'),
                          !isFirst && !isLast && (isOwn ? 'rounded-l-2xl rounded-r-md' : 'rounded-r-2xl rounded-l-md'),
                        )}>
                          <p className="text-sm italic text-muted-foreground">{t('messages.deleted')}</p>
                        </div>
                      </div>
                    );
                  }

                  // Skip empty messages with no content (but keep recently-completed
                  // messages visible — their content arrives via message.updated shortly).
                  //
                  // `cancelled` (and `error`) intentionally fall through to the
                  // dedicated cancelled / error UI below. Without this exclusion the
                  // dots keep spinning forever after the user hits Stop on a run that
                  // produced no partial content (e.g. while the LLM was still thinking).
                  if (!message.content && (!message.contentItems || message.contentItems.length === 0) && !message.mediaUrl) {
                    if (msgStatus === 'complete' || msgStatus === 'streaming') {
                      if (STREAM_DBG) {
                        const key = `${message.id}:${msgStatus}`;
                        if (!dotsRenderSeen.has(key)) {
                          dotsRenderSeen.add(key);
                          const hasOverlayForMsg = (() => {
                            if (message.uampEventId && state.overlays.has(message.uampEventId)) return true;
                            for (const [, o] of state.overlays) if (o.messageId === message.id) return true;
                            return false;
                          })();
                          sdbg(`RENDER dots: messageId=${message.id} status=${msgStatus} uampEventId=${message.uampEventId ?? 'none'} hasOverlayForMsg=${hasOverlayForMsg} totalMessages=${state.messages.length} overlaysCount=${state.overlays.size}`);
                        }
                      }
                      return (
                        <div key={message.id} className={cn('relative max-w-full', !isFirst && 'mt-0.5')}>
                          <div className="flex items-center gap-1 px-1 pt-2.5">
                            <span className="thinking-dot h-1 w-1 rounded-full bg-muted-foreground" style={{ animationDelay: '0ms' }} />
                            <span className="thinking-dot h-1 w-1 rounded-full bg-muted-foreground" style={{ animationDelay: '150ms' }} />
                            <span className="thinking-dot h-1 w-1 rounded-full bg-muted-foreground" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      );
                    }
                    if (msgStatus !== 'cancelled' && msgStatus !== 'error') {
                      return null;
                    }
                  }

                  // Error placeholder — gentle, non-alarming presentation.
                  // `errorCode` is the machine-readable classification written
                  // by the agent router; the raw-string sniffing only remains
                  // as a fallback for rows persisted before it existed.
                  const rawError = (message.metadata?.error as string) || '';
                  const msgErrorCode = (message.metadata?.errorCode as string) || '';
                  const isCancelled = msgStatus === 'cancelled';
                  const isInsufficientBalance = msgErrorCode === 'insufficient_balance';
                  const isMaxIterations = msgErrorCode === 'max_iterations' || /maximum.*(?:processing|iteration|steps)/i.test(rawError);
                  const isContextOverflow = msgErrorCode === 'context_overflow';
                  const isGenericError = !isCancelled && !isMaxIterations && !isInsufficientBalance && !isContextOverflow
                    && (msgErrorCode === 'timeout' || msgErrorCode === 'agent_unavailable'
                      || !rawError || rawError === 'Empty response' || /did not return|took too long|unavailable/i.test(rawError));
                  const errorFriendlyMsg = isInsufficientBalance
                    ? t('messages.insufficientBalance')
                    : isCancelled
                    ? t('messages.cancelled')
                    : isMaxIterations
                    ? t('messages.maxIterationsError')
                    : isContextOverflow
                    ? t('messages.contextOverflowError')
                    : isGenericError
                      ? t('messages.agentErrorGeneric')
                      : rawError;
                  const hasPartialContent = !!(message.metadata?.partial) &&
                    (!!message.content || (message.contentItems && message.contentItems.length > 0));

                  if (msgStatus === 'cancelled' || msgStatus === 'error') {
                    if (!hasPartialContent) {
                      return (
                        <div key={message.id} className={cn('relative max-w-full', !isFirst && 'mt-0.5')}>
                          <div className={cn('px-4 py-2.5 max-w-full overflow-hidden bg-muted/60 text-muted-foreground', 'rounded-2xl rounded-bl-md')}>
                            <div className="flex items-start gap-2.5">
                              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 opacity-60" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm">{errorFriendlyMsg}</p>
                                {isInsufficientBalance ? (
                                  // Retrying without funds cannot succeed — offer the
                                  // one action that helps instead.
                                  <button onClick={() => setShowTopUpDialog(true)}
                                    className="mt-2 bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-medium px-3 py-1.5 rounded-md transition-colors">
                                    {t('messages.addCredits')}
                                  </button>
                                ) : (
                                  <button onClick={() => { setInput(messages.filter(m => m.senderId === user?.id).at(-1)?.content || ''); requestAnimationFrame(resizeTextarea); inputRef.current?.focus(); }}
                                    className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                                    <RefreshCw className="h-3 w-3" /> {t('messages.tryAgain')}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    // hasPartialContent: fall through to normal rendering with a footer
                  }

                  return (
                    <div key={message.id} className={cn('relative group/msg max-w-full', !isFirst && 'mt-0.5')}>
                      <div className={cn(
                        'px-4 py-2 max-w-full overflow-hidden',
                        isOwn ? 'bg-primary/15 text-foreground' : 'bg-muted',
                        isFirst && isLast && (isOwn ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-bl-md'),
                        isFirst && !isLast && (isOwn ? 'rounded-t-2xl rounded-bl-2xl rounded-br-md' : 'rounded-t-2xl rounded-br-2xl rounded-bl-md'),
                        !isFirst && isLast && (isOwn ? 'rounded-b-2xl rounded-tl-2xl rounded-tr-md' : 'rounded-b-2xl rounded-tr-2xl rounded-tl-md'),
                        !isFirst && !isLast && (isOwn ? 'rounded-l-2xl rounded-r-md' : 'rounded-r-2xl rounded-l-md'),
                      )}>
                        {message.replyTo && (
                          <div className="text-xs mb-1 pb-1 border-b border-border">
                            <span className="opacity-70">↩ {message.replyTo.senderUsername}:</span>{' '}
                            {message.replyTo.content.slice(0, 50)}{message.replyTo.content.length > 50 && '...'}
                          </div>
                        )}

                        {isEditing ? (
                          <div className="space-y-2">
                            <textarea ref={editTextareaRef} value={editContent} onChange={(e) => setEditContent(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEditMessage(); } if (e.key === 'Escape') cancelEditMessage(); }}
                              className="w-full min-h-[60px] bg-background/50 border border-border rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                              rows={Math.min(editContent.split('\n').length + 1, 6)} />
                            <div className="flex gap-1.5 justify-end">
                              <button onClick={cancelEditMessage} className="px-2 py-1 text-xs rounded-md hover:bg-accent transition-colors text-muted-foreground">{t('actions.cancel')}</button>
                              <button onClick={saveEditMessage} disabled={!editContent.trim()} className="px-2 py-1 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50">{t('actions.save')}</button>
                            </div>
                          </div>
                        ) : <PersistedMessageContent message={message} isOwn={isOwn} handlers={persistedContentHandlersRef} />}

                        {message.metadata?.type === 'payment_required' && (
                          <div className="mt-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                            <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-2">{t('messages.insufficientBalance')}</p>
                            <div className="flex gap-2">
                              <button onClick={() => setShowTopUpDialog(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-medium px-3 py-1.5 rounded-md transition-colors">{t('messages.addCredits')}</button>
                            </div>
                          </div>
                        )}
                        {!message.contentItems && message.mediaUrl && (
                          <div className="mt-2">
                            {message.mediaType?.startsWith('image') ? <Image src={message.mediaUrl} alt={t('chatView.mediaAlt')} width={600} height={400} unoptimized className="rounded-lg max-w-full w-auto" sizes="(max-width: 640px) 100vw, 600px" /> :
                             message.mediaType?.startsWith('video') ? <video src={message.mediaUrl} controls className="rounded-lg max-w-full" /> :
                             message.mediaType?.startsWith('audio') ? <audio src={message.mediaUrl} controls className="w-full max-w-xs" /> : null}
                          </div>
                        )}
                      </div>
                      {!!message.metadata?.failed && (
                        <button
                          onClick={() => handleRetry(message.id)}
                          className="mt-1 px-3 py-1.5 flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 transition-colors"
                        >
                          <AlertCircle className="h-3 w-3 shrink-0" />
                          <span>{t('chatView.notSentTap')}</span>
                        </button>
                      )}
                      {(msgStatus === 'cancelled' || msgStatus === 'error') && !!(message.metadata?.partial) && (
                        <div className="mt-1 px-3 py-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                          <AlertCircle className="h-3 w-3 shrink-0 opacity-50" />
                          <span className="flex-1">{errorFriendlyMsg}</span>
                          <button onClick={() => { setInput(messages.filter(m => m.senderId === user?.id).at(-1)?.content || ''); requestAnimationFrame(resizeTextarea); inputRef.current?.focus(); }}
                            className="inline-flex items-center gap-1 text-primary hover:underline">
                            <RefreshCw className="h-3 w-3" /> {t('messages.tryAgain')}
                          </button>
                        </div>
                      )}
                      <MessageLinkPreviews message={message} />
                    </div>
                  );
                })}

                {!isStreaming && (
                  <div className={cn('flex items-center gap-1 mt-1 text-[10px] px-1', isOwn ? 'text-muted-foreground justify-end' : 'text-muted-foreground')}>
                    {isOwn && (() => {
                      const lastMsg = group.messages[group.messages.length - 1];
                      if (!lastMsg || lastMsg.isDeleted) return null;
                      const canEdit = lastMsg.role === 'user' && !!(lastMsg.content || lastMsg.contentItems?.every(i => i.type === 'text'));
                      return <MessageActions messageId={lastMsg.id} isOwn={isOwn} canEdit={canEdit} onEdit={() => startEditMessage(lastMsg)} onDelete={() => handleDeleteMessage(lastMsg.id)} />;
                    })()}
                    <span>{new Date(group.lastTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    {group.messages.some(m => m.isEdited) && <span className="opacity-60">({t('messages.edited')})</span>}
                    {isOwn && (() => {
                      // Per-message owner → platform delivery status pill.
                      // Renders only on the last bubble of an own group when the
                      // message was dispatched to a bridged platform; absent
                      // metadata means non-bridged or owner opted out (so we
                      // intentionally render nothing).
                      const lastMsg = group.messages[group.messages.length - 1];
                      if (!lastMsg || lastMsg.isDeleted) return null;
                      const meta = lastMsg.metadata as
                        | { deliveryStatus?: 'pending' | 'sent' | 'failed'; deliveryError?: string; externalMessageId?: string; source?: string }
                        | undefined;
                      const status = meta?.deliveryStatus;
                      if (!status) return null;
                      const sourceLabel = (() => {
                        const s = meta?.source ?? (typeof chat?.metadata?.source === 'string' ? chat?.metadata?.source : undefined);
                        if (!s) return 'platform';
                        return HEADER_SOURCE_BADGE[s as ChatSource]?.label ?? s;
                      })();
                      const sourceSlug = (() => {
                        const s = meta?.source ?? (typeof chat?.metadata?.source === 'string' ? chat?.metadata?.source : undefined);
                        if (!s) return undefined;
                        return HEADER_SOURCE_BADGE[s as ChatSource]?.slug;
                      })();
                      if (status === 'pending') {
                        return (
                          <span className="inline-flex items-center gap-1 opacity-70">
                            <Spinner size="xs" />
                            <span>Sending to {sourceLabel}</span>
                          </span>
                        );
                      }
                      if (status === 'sent') {
                        return (
                          <Tip label={meta?.externalMessageId ? `Delivered (msg ${meta.externalMessageId})` : 'Delivered'}>
                            <span className="inline-flex items-center gap-1 opacity-70">
                              {sourceSlug && <ProviderIcon slug={sourceSlug} size={10} />}
                              <Check className="h-3 w-3" />
                            </span>
                          </Tip>
                        );
                      }
                      // status === 'failed'
                      const firstText = lastMsg.contentItems?.find(
                        (i): i is TextContent => i.type === 'text',
                      );
                      const retryContent = lastMsg.content || firstText?.text || '';
                      return (
                        <Tip label={meta?.deliveryError ?? `Failed to deliver to ${sourceLabel}`}>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!chatId || !retryContent) return;
                              try {
                                await fetch(`/api/messages/${chatId}`, {
                                  method: 'POST',
                                  headers: sendHeaders,
                                  body: JSON.stringify({ content: retryContent, bridgeOutbound: true }),
                                });
                              } catch (err) {
                                console.error('[chat-view] retry-bridge error:', (err as Error).message);
                              }
                            }}
                            className="inline-flex items-center gap-1 text-red-500 hover:text-red-600 transition-colors"
                          >
                            <AlertCircle className="h-3 w-3" />
                            <span>{t('chatView.notSentRetry')}</span>
                          </button>
                        </Tip>
                      );
                    })()}
                    {!isOwn && (() => {
                      const lastMsg = group.messages[group.messages.length - 1];
                      if (!lastMsg || lastMsg.isDeleted) return null;
                      return <MessageActions messageId={lastMsg.id} isOwn={isOwn} canEdit={false} onDelete={() => handleDeleteMessage(lastMsg.id)} />;
                    })()}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* THE HELD TURN (2026-10-08): one plain line while the daily
            spending limit holds the agent's answer, in the system notice's
            own style, at the foot of the conversation where the answer
            would appear. Gone the moment the turn resumes or ends. */}
        {heldTurn && (
          <div
            data-testid="spending-hold-line"
            className="mx-3 mt-1 mb-1 px-3 py-2 text-xs rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-200"
          >
            {t('messages.spendingHold')}
          </div>
        )}
        <div className="h-4 shrink-0" />
      </StickToBottom.Content>
      <ScrollToBottomButton />
      </StickToBottom>

      {/* Critical payment-required banner (Tier A) - sits above the composer */}
      {chatId && <ChatPaymentBanner chatId={chatId} />}

      {/* Input area */}
      <div className="shrink-0 bg-background">
        <div className="h-5 px-4 flex items-center">
          {typingUsers.size > 0 ? (
            <span className="text-[11px] text-muted-foreground">
              <span className="font-medium">{Array.from(typingUsers.values()).join(', ')}</span> {typingUsers.size === 1 ? t('messages.isTyping') : t('messages.areTyping')}
            </span>
          ) : null}
        </div>
        {sendError && (
          <div className="px-3 py-2 flex items-center justify-between gap-2 bg-red-500/10 border-t border-red-500/20 text-sm text-red-700 dark:text-red-300">
            <span className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{sendError}</span>
            </span>
            <button type="button" onClick={() => setSendError(null)} className="shrink-0 p-1 rounded hover:bg-red-500/20" aria-label={t('actions.dismiss')}><X className="h-4 w-4" /></button>
          </div>
        )}
        {agentUnreachable && (
          <div className="px-3 py-2 flex items-center justify-between gap-2 bg-amber-500/10 border-t border-amber-500/20 text-sm text-amber-800 dark:text-amber-200">
            <span className="flex items-center gap-2">
              <Bot className="h-4 w-4 shrink-0" />
              <span><strong>@{agentUnreachable.agentUsername}</strong> {agentUnreachable.reason ?? t('messages.agentOffline')}</span>
            </span>
            <button type="button" onClick={() => setAgentUnreachable(null)} className="shrink-0 p-1 rounded hover:bg-amber-500/20" aria-label={t('actions.dismiss')}><X className="h-4 w-4" /></button>
          </div>
        )}
        {bridgedPaymentNotice && (
          <div className="px-3 py-2 flex items-center justify-between gap-2 bg-blue-500/10 border-t border-blue-500/20 text-sm text-blue-800 dark:text-blue-200">
            <span className="flex items-center gap-2">
              <Bot className="h-4 w-4 shrink-0" />
              <span>{bridgedPaymentNotice}</span>
            </span>
            <button type="button" onClick={() => setBridgedPaymentNotice(null)} className="shrink-0 p-1 rounded hover:bg-blue-500/20" aria-label={t('actions.dismiss')}><X className="h-4 w-4" /></button>
          </div>
        )}
        {pendingFiles.length > 0 && (
          <div className="px-4 py-2 flex flex-wrap gap-2">
            {pendingFiles.map((file, idx) => {
              const isImage = file.type.startsWith('image/');
              const isVideo = file.type.startsWith('video/');
              const isAudio = file.type.startsWith('audio/');
              const is3D = file.type.includes('gltf') || file.type.includes('usdz') || /\.(glb|gltf|usdz)$/i.test(file.name);
              const ext = file.name.split('.').pop()?.toUpperCase() || 'FILE';

              if (isImage && pendingFileUrls[idx]) {
                return (
                  <div key={`${file.name}-${idx}`} className="relative h-16 w-16 rounded-lg overflow-hidden bg-muted group">
                    <img src={pendingFileUrls[idx]!} alt={file.name} className="h-full w-full object-cover" />
                    <button onClick={() => removeFile(idx)} className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-2.5 w-2.5" /></button>
                  </div>
                );
              }
              if (isVideo && pendingFileUrls[idx]) {
                return (
                  <div key={`${file.name}-${idx}`} className="relative h-16 w-16 rounded-lg overflow-hidden bg-muted group">
                    <video src={pendingFileUrls[idx]!} className="h-full w-full object-cover" preload="metadata" muted />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="h-6 w-6 rounded-full bg-black/50 flex items-center justify-center"><Film className="h-3 w-3 text-white" /></div></div>
                    <button onClick={() => removeFile(idx)} className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-2.5 w-2.5" /></button>
                  </div>
                );
              }
              if (isAudio) {
                return (
                  <div key={`${file.name}-${idx}`} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-muted rounded-full text-xs group">
                    <Music className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span className="truncate max-w-[100px]">{file.name}</span>
                    <button onClick={() => removeFile(idx)} className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity"><X className="h-3 w-3" /></button>
                  </div>
                );
              }
              return (
                <div key={`${file.name}-${idx}`} className="flex items-center gap-2 px-2.5 py-1.5 bg-muted rounded-lg text-xs group">
                  <div className={cn("h-8 w-8 rounded flex items-center justify-center shrink-0", is3D ? "bg-purple-500/10" : "bg-blue-500/10")}>
                    {is3D ? <Box className="h-4 w-4 text-purple-500" /> : <FileText className="h-4 w-4 text-blue-500" />}
                  </div>
                  <div className="min-w-0">
                    <span className="truncate block max-w-[100px] font-medium">{file.name}</span>
                    <span className="text-muted-foreground">{ext}</span>
                  </div>
                  <button onClick={() => removeFile(idx)} className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity"><X className="h-3 w-3" /></button>
                </div>
              );
            })}
          </div>
        )}
        {uploading && (
          <div className="px-4 py-1">
            <div className="h-1 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all duration-300 rounded-full" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}
        {uploadError && (
          <div className="px-4 py-1.5 flex items-center justify-between gap-2 text-xs text-red-600 dark:text-red-400 bg-red-500/10 border-t border-red-500/20">
            <span>{uploadError}</span>
            <button onClick={() => setUploadError(null)} className="shrink-0 p-0.5 rounded hover:bg-red-500/20"><X className="h-3 w-3" /></button>
          </div>
        )}
        {(() => {
          // Composer banner for bridged DMs. Renders only when:
          //   • the chat is a 1:1 DM (group chats don't bridge — there's no
          //     single contact to forward to)
          //   • chat.metadata.source is a provider in OWNER_OUTBOUND_PROVIDERS
          //     (mirror of `lib/messaging/owner-outbound-bridge.ts`).
          // The Switch is a mode switch the server interprets via
          // `bridgeOutbound`:
          //   ON  → message goes to the platform; agent stays silent.
          //   OFF → message stays in the portal; agent replies inline.
          const sourceRaw = chat?.metadata?.source;
          const source = typeof sourceRaw === 'string' ? sourceRaw : undefined;
          const isBridged =
            chat?.type === 'dm' && !!source && OWNER_OUTBOUND_PROVIDERS.has(source);
          if (!isBridged) return null;
          const cfg = HEADER_SOURCE_BADGE[source as ChatSource];
          const platformLabel = cfg?.label ?? 'the platform';
          const contactName =
            (typeof chat?.metadata?.contactDisplayName === 'string' && chat?.metadata?.contactDisplayName) ||
            chat?.participants.find((p) => p.userId !== user?.id)?.displayName ||
            cfg?.label ||
            'this contact';
          const switchId = 'bridge-outbound-switch';
          return (
            <div className="px-4 py-1.5 flex items-center justify-between gap-3 text-xs border-t border-border bg-muted/30">
              <label
                htmlFor={switchId}
                className={cn(
                  'flex items-center gap-1.5 min-w-0 cursor-pointer select-none',
                  bridgeOutbound ? 'text-muted-foreground' : 'text-muted-foreground/70'
                )}
              >
                {cfg && <ProviderIcon slug={cfg.slug} size={12} />}
                <span className="truncate">
                  {bridgeOutbound
                    ? `Replies are sent to ${contactName} on ${platformLabel}`
                    : `Replies stay in this portal chat — agent will reply (not sent to ${platformLabel})`}
                </span>
              </label>
              <div className="shrink-0 flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground hidden sm:inline">
                  Send to {platformLabel}
                </span>
                <button
                  id={switchId}
                  type="button"
                  role="switch"
                  aria-checked={bridgeOutbound}
                  aria-label={`Send replies to ${platformLabel}`}
                  disabled={persistingDeliveryMode}
                  onClick={async () => {
                    const next = !bridgeOutbound;
                    setBridgeOutbound(next);
                    if (!chatId) return;
                    setPersistingDeliveryMode(true);
                    try {
                      // Persist the choice on chat.metadata.deliveryMode so the
                      // toggle survives reloads and so the dispatcher honours
                      // the same routing decision the user just made.
                      const r = await fetch(`/api/messages/${chatId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          deliveryMode: next ? 'bridge' : 'portal_only',
                        }),
                      });
                      if (!r.ok) {
                        // Roll back optimistic toggle on failure to keep the
                        // UI honest about server state.
                        setBridgeOutbound(!next);
                      }
                    } catch {
                      setBridgeOutbound(!next);
                    } finally {
                      setPersistingDeliveryMode(false);
                    }
                  }}
                  className={cn(
                    'relative inline-flex h-5 w-9 items-center cursor-pointer rounded-full transition-colors shrink-0',
                    bridgeOutbound ? 'bg-primary' : 'bg-muted-foreground/30 ring-1 ring-border',
                    persistingDeliveryMode && 'opacity-60 cursor-wait'
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform',
                      bridgeOutbound ? 'translate-x-4.5' : 'translate-x-0.5'
                    )}
                  />
                </button>
              </div>
            </div>
          );
        })()}
        <div className="px-3 pb-3 flex items-end gap-2">
          <input ref={fileInputRef} type="file" multiple className="hidden" accept="image/*,video/*,audio/*,.pdf,.txt,.md,.doc,.docx,.xlsx,.csv,.json,.glb,.gltf,.usdz"
            onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }} />
          <div className="flex-1 relative">
            <textarea ref={textareaRefCallback} value={input} onChange={handleInputChange} onKeyDown={handleKeyDown} onPaste={handlePaste}
              placeholder={t('messages.typeMessage')} rows={1}
              aria-label={t('chatView.messageAria')}
              // Composer textarea has its OWN sizing pipeline (`resizeTextarea` writes
              // an inline `height` based on `scrollHeight`, clamped to 180). We
              // deliberately skip the global `.input` utility here because that class
              // bakes in `display: flex` + a fixed `height: 2.25rem` — both designed
              // for single-line `<input>` elements. On a `<textarea>` those rules
              // were causing the composer to render at 180px tall in narrow embedded
              // contexts (delegation puck → expanded agent widget): `display: flex`
              // makes `scrollHeight` measure the element as a flex container under
              // an unsettled flex parent, which inflates to ~the parent's height,
              // and then our resize clamps to the 180 cap and pins it there
              // permanently — the composer never shrinks back to the natural
              // single-line height. Inlining the visual tokens (border, radius,
              // background, focus ring, padding) keeps the look identical while
              // letting `field-sizing: content` + the JS resize loop work.
              className={cn(
                'block w-full resize-none overflow-hidden',
                'rounded-md border border-border bg-background text-sm',
                'py-3 pl-4 pr-11 min-h-[44px] max-h-[180px]',
                'placeholder:text-muted-foreground',
                'focus:outline-2 focus:outline-ring focus:border-ring',
                'transition-[border-color,box-shadow] duration-150',
                inputPulsating && 'animate-pulse-border ring-2 ring-primary/60',
              )} />
            <Tip label={t('messages.attachFile')}>
            <button type="button" onClick={() => { const el = fileInputRef.current; if (el) { el.value = ''; el.click(); } }} disabled={uploading}
              className="absolute right-2 bottom-1.5 h-8 w-8 rounded-lg flex items-center justify-center hover:bg-accent/50 text-muted-foreground hover:text-foreground transition-colors">
              <Paperclip className="h-4 w-4" />
            </button>
            </Tip>
          </div>
          {!input.trim() && pendingFiles.length === 0 && isAgentWorking && (
            <Tip label={t('messages.stopGenerating')}>
            <button onClick={handleStop} className="h-11 w-11 rounded-xl flex items-center justify-center transition-all shrink-0 bg-destructive text-destructive-foreground hover:bg-destructive/90">
              <Square className="h-4 w-4" />
            </button>
            </Tip>
          )}
          {!isAgentWorking && (
            <button onClick={handleSend} disabled={(!input.trim() && pendingFiles.length === 0) || sending || uploading}
              aria-label={t('chatView.sendMessage')}
              className={cn('h-11 w-11 rounded-xl flex items-center justify-center transition-all shrink-0',
                (input.trim() || pendingFiles.length > 0) ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-muted text-muted-foreground cursor-not-allowed')}>
              {sending || uploading ? <Spinner size="lg" /> : <Send className="h-5 w-5" />}
            </button>
          )}
        </div>
      </div>

      <SubscribeDialog open={showTopUpDialog} onClose={() => setShowTopUpDialog(false)} />
      {lightboxItems.length > 0 && <Lightbox items={lightboxItems} initialIndex={lightboxIndex} onClose={closeLightbox} />}
    </div>
    </ChatHostProvider>
  );
}
